// Step 3 — Content Builder (EMAIL-007 / EMAIL-008 / EMAIL-009 / EMAIL-CREATE-012/014/015/016).
//
// Two phases per the Step 3 screenshot set:
//   1. Template GALLERY — 8 numbered template cards with previews, gold Select
//      buttons, current selection, and compact preview frames.
//   2. BUILDER — 3-pane layout (Blocks palette / email canvas / settings) with
//      real per-block settings panels driven by the canonical 27-block model in
//      emailBuilderData.ts.
//
// Quirk corrections applied from the mocks (13_DESIGN_JUDGMENT rules — each
// one logged in the module changelog):
//   - "Best For:Time-sensitive" -> "Best For: Time-sensitive" (missing space).
//   - "7.  FROM THE DESK" double space -> single space (mock kerning artifact).
//   - "Karen Hattle" -> "Karen Hattie" (sample-name typo; other shots use Hattie).
//   - "Template Settngs" -> "Template Settings" (mock tab typo).
//   - Change-event popup subtitle "Change to another event by Select an
//     upcoming event..." -> "Select an upcoming event to feature in your
//     newsletter template." (mock grammar artifact).

import React, {
  createContext,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { RoutePaths } from "../../../enumStrings";
import { ModalShell } from "../../common/dashboard/ModalShell";
import { ConfirmDialog } from "../../common/dashboard/ConfirmDialog";
import { CircularProgress, Radio } from "@mui/material";
import { inlineChoiceControlSx } from "../../common/dashboard/formStyles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ColorizeIcon from "@mui/icons-material/Colorize";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  CmpEventStatusEnum,
  CmpEventVisibilityEnum,
  GetCmpManageEventsQuery,
  useGetCmpManageEventsQuery,
  useGetCmpEmailBuilderEventDetailLazyQuery,
  useGetCmpEmailBuilderPerksQuery,
  WhereToRedeemEnum,
} from "../../../graphql/types";
import { useCmpGlobalSettings } from "../../../hooks/useCmpSettingsSection";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import { emailImageAccept, getEmailImageFileError } from "./emailAssetUpload";
import {
  AdvertisingPdfSettings,
  NewsroomHighlightItem,
  NewMemberItem,
  createGalleryItem,
  getEmailBuilderIconDataUrl,
  ArticleListSettings,
  EmailBannerSettings,
  EmailBuilderBlock,
  EmailBuilderBlockOf,
  EmailBuilderBlockSettingsMap,
  EmailFooterProfile,
  EmailBuilderBlockType,
  EmailBuilderEventItem,
  EmailBuilderIconKey,
  EmailBuilderState,
  UpdateEmailBuilderState,
  EmailTemplateKey,
  EmailTemplatePreset,
  FourGridIconAndTitleSettings,
  InquiriesSettings,
  MemberSpotlightSettings,
  SixGridIconAndTitleSettings,
  SplitArticleWithBorderSettings,
  SponsorsListSettings,
  TextAndEmailContentSettings,
  TextHeroBannerSettings,
  buildEmailBuilderEventLinkUrl,
  buildRichTextValue,
  getRichTextParts,
  createArticleListItem,
  createEmailBuilderBlock,
  createEventItem,
  createIconGridItem,
  createKeyLinkItem,
  KeyLinkItem,
  EmailBuilderPerkItem,
  createMemberSpotlightItem,
  createSplitArticleItem,
  createSponsorItem,
  emailBuilderBlockLabels,
  emailBuilderBlockTagLabels,
  getEmailBuilderIconOptions,
  getEmailBuilderFooterBlock,
  replaceEmailBuilderContentBlocks,
  emailBuilderPaletteItems,
  getEmailBuilderPaletteSections,
  NEWSROOM_BLOCK_MAX_ARTICLES,
  emailAdBannerSizes,
  getEmailAdBannerSlots,
  formatEmailLogoDate,
  applyNewEmailBuilderBlockDefaults,
  migrateLegacySignatureSettings,
  emailGalleryTemplatePresets,
  emailTemplatePresets,
  getEmailThemeQuickPalette,
  getReadableEmailTextColor,
  renderEmailBuilderBlock,
  renderEmailBuilderHeader,
  richTextToHtml,
  SimpleEmailBlockSettings,
} from "./emailBuilderData";
import RichTextEditor from "../../common/FormComponents/RichTextEditor";
import { buildEmailRichTextValue, emailRichTextToPlainText, isEmailRichText } from "./emailRichText";
import EmailFooterPreview from "./EmailFooterPreview";
import EmailBlockThumbnail from "./EmailBlockThumbnail";
import {
  EmailFormState,
  EmailAudienceMemberOption,
  emailTypeForTemplate,
} from "./emailMockData";
import { normalizeEmailLinkUrl } from "./emailSafeLink";
import { getPerkWeekDetailFields, PERK_WEEK_GOLD } from "./emailPerkOfTheWeek";
import {
  getNewMembersPickerCandidates,
  getNewMembersWindowDays,
  getRecentNewMembers,
  NEW_MEMBERS_WINDOW_DAYS,
  refreshNewMembersItems,
} from "./emailNewMembersBlock";
import {
  UpdateEmailField,
  inputClassName,
  primaryButtonClassName,
  sectionClassName,
  stepDescriptionClassName,
  stepTitleClassName,
} from "./emailWizardShared";
import {
  emailBuilderBlockReorderInstructionsId as blockReorderInstructionsId,
  useEmailBuilderBlockReorder,
} from "./useEmailBuilderBlockReorder";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloCallerContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../utils/userFacingError";

// ---------------------------------------------------------------------------
// Shared class names + small helpers
// ---------------------------------------------------------------------------

const builderInputClassName =
  "w-full rounded-lg border border-[#ECECEC] bg-white px-3 py-2.5 text-[13px] leading-[normal] text-[#0A0A0A] outline-none focus:border-cpaGold disabled:opacity-60";

const settingLabelClassName =
  "block text-[11px] font-bold uppercase tracking-[.06em] text-[#0A0A0A]";
const colorRowLabelClassName = "text-[13px] font-normal text-[#0A0A0A]";
const editorFontFamily = '"DM Sans", sans-serif';
const goldFullWidthButtonClassName =
  "flex min-h-[46px] w-full items-center justify-center gap-1 rounded-md bg-cpaGold px-4 text-[15px] font-extrabold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";
const itemTrashButtonClassName =
  "flex shrink-0 items-center justify-center border-0 bg-transparent p-0.5 text-[#F21B1B] transition disabled:cursor-not-allowed disabled:opacity-60 [&_.MuiSvgIcon-root]:text-[14px]";

const maxBuilderPdfBytes = 10 * 1024 * 1024;

type HsvaColor = { h: number; s: number; v: number; a: number };

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseHexColor = (
  value: string,
): { r: number; g: number; b: number; a: number } | null => {
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
  };
};

const channelToHex = (channel: number) =>
  clampNumber(Math.round(channel), 0, 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

const rgbaToHexColor = (r: number, g: number, b: number, a: number) =>
  `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}${
    a >= 1 ? "" : channelToHex(a * 255)
  }`;

const rgbToHsv = (r: number, g: number, b: number) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

// "Text color will be automatically selected based on background color."

type EyeDropperResult = { sRGBHex: string };
type EyeDropperConstructor = new () => {
  open: () => Promise<EyeDropperResult>;
};

const getEyeDropperConstructor = (): EyeDropperConstructor | null =>
  typeof window !== "undefined" && "EyeDropper" in window
    ? (window as unknown as { EyeDropper: EyeDropperConstructor }).EyeDropper
    : null;

const builderColorSliderCss = `
.cmp-builder-color-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 9999px;
  outline: none;
}
.cmp-builder-color-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
.cmp-builder-color-slider::-moz-range-thumb {
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
`;

const BuilderColorPicker = ({
  color,
  onChange,
  appearance,
}: {
  color: string;
  onChange: (hex: string) => void;
  appearance?: "builder";
}) => {
  const compact = appearance === "builder";
  const initial = parseHexColor(color) || { r: 0, g: 0, b: 0, a: 1 };
  const [hsva, setHsva] = useState<HsvaColor>(() => ({
    ...rgbToHsv(initial.r, initial.g, initial.b),
    a: initial.a,
  }));
  const [hexDraft, setHexDraft] = useState(() =>
    rgbaToHexColor(initial.r, initial.g, initial.b, initial.a),
  );

  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const roundedRgb = {
    r: Math.round(rgb.r),
    g: Math.round(rgb.g),
    b: Math.round(rgb.b),
  };
  const currentHex = rgbaToHexColor(
    roundedRgb.r,
    roundedRgb.g,
    roundedRgb.b,
    hsva.a,
  );
  const eyeDropperSupported = !!getEyeDropperConstructor();

  const applyHsva = (next: HsvaColor) => {
    setHsva(next);
    const nextRgb = hsvToRgb(next.h, next.s, next.v);
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, next.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyRgbChannel = (channel: "r" | "g" | "b", value: number) => {
    const nextRgb = {
      ...roundedRgb,
      [channel]: clampNumber(Math.round(value) || 0, 0, 255),
    };
    setHsva({ ...rgbToHsv(nextRgb.r, nextRgb.g, nextRgb.b), a: hsva.a });
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, hsva.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyHexDraft = (value: string) => {
    setHexDraft(value);
    const parsed = parseHexColor(value);
    if (!parsed) return;
    setHsva({ ...rgbToHsv(parsed.r, parsed.g, parsed.b), a: parsed.a });
    onChange(rgbaToHexColor(parsed.r, parsed.g, parsed.b, parsed.a));
  };

  const pickWithEyeDropper = async () => {
    const EyeDropperCtor = getEyeDropperConstructor();
    if (!EyeDropperCtor) return;
    try {
      const result = await new EyeDropperCtor().open();
      applyHexDraft(result.sRGBHex);
    } catch {
      // Color picking was canceled.
    }
  };

  const updateSaturationFromPointer = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    applyHsva({
      ...hsva,
      s: clampNumber((event.clientX - bounds.left) / bounds.width, 0, 1),
      v: clampNumber(1 - (event.clientY - bounds.top) / bounds.height, 0, 1),
    });
  };

  return (
    <div>
      <style>{builderColorSliderCss}</style>
      <div
        className={`relative w-full cursor-crosshair ${compact ? "h-[150px] rounded-lg" : "h-36 rounded"}`}
        role="presentation"
        style={{
          background: `linear-gradient(to top, #000000, rgba(0, 0, 0, 0)), linear-gradient(to right, #ffffff, hsl(${Math.round(
            hsva.h,
          )}, 100%, 50%))`,
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateSaturationFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) updateSaturationFromPointer(event);
        }}
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[2.5px] border-white shadow-[0_0_4px_rgba(0,0,0,.4)]"
          style={{
            left: `${hsva.s * 100}%`,
            top: `${(1 - hsva.v) * 100}%`,
            backgroundColor: `rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})`,
          }}
        />
      </div>
      <div
        className={`mt-3 flex items-center ${compact ? "gap-2.5" : "gap-3"}`}
      >
        {!compact || eyeDropperSupported ? (
          <button
            type="button"
            onClick={pickWithEyeDropper}
            disabled={!eyeDropperSupported}
            title={
              eyeDropperSupported
                ? "Pick a colour from the screen"
                : "Your browser cannot pick a colour from the screen. Choose a colour with the colour controls instead."
            }
            className={
              compact
                ? "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                : "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-[#2F6FED] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            }
            style={compact ? { backgroundColor: currentHex } : undefined}
          >
            <ColorizeIcon
              sx={compact ? { fontSize: 15 } : undefined}
              fontSize="small"
            />
          </button>
        ) : null}
        <div className="flex-1 space-y-2.5">
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={Math.round(hsva.h)}
            aria-label="Hue"
            onChange={(event) =>
              applyHsva({ ...hsva, h: Number(event.target.value) })
            }
            className="cmp-builder-color-slider w-full"
            style={{
              background:
                "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            }}
          />
          {!compact ? (
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(hsva.a * 100)}
              aria-label="Opacity"
              onChange={(event) =>
                applyHsva({ ...hsva, a: Number(event.target.value) / 100 })
              }
              className="cmp-builder-color-slider w-full"
              style={{
                background: `linear-gradient(to right, rgba(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b}, 0), rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})), repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 12px 12px`,
              }}
            />
          ) : null}
        </div>
      </div>
      <div
        className={`mt-3 items-end gap-2 ${compact ? "grid grid-cols-[1.4fr_1fr_1fr_1fr]" : "flex"}`}
      >
        <label className={compact ? "flex min-w-0 flex-col gap-1" : "block flex-1"}>
          <span
            className={
              compact
                ? "text-[10px] font-bold leading-[normal] text-[#0A0A0A]"
                : "text-[11px] font-extrabold uppercase text-gray-500"
            }
          >
            HEX
          </span>
          <input
            aria-label="HEX"
            value={hexDraft}
            onChange={(event) => applyHexDraft(event.target.value)}
            onBlur={() => setHexDraft(currentHex)}
            className={
              compact
                ? "w-full min-w-0 rounded-lg border border-[#ECECEC] px-0.5 py-2 text-center text-[12px] leading-[normal] text-[#0A0A0A] outline-none focus:border-cpaGold"
                : "mt-1 h-9 w-full rounded border border-gray-300 px-2 text-[13px] text-gray-950 outline-none focus:border-cpaGold"
            }
          />
        </label>
        {(["r", "g", "b"] as const).map((channel) => (
          <label
            key={channel}
            className={compact ? "flex min-w-0 flex-col gap-1" : "block w-[52px]"}
          >
            <span
              className={
                compact
                  ? "text-[10px] font-bold leading-[normal] text-[#0A0A0A]"
                  : "text-[11px] font-extrabold uppercase text-gray-500"
              }
            >
              {channel.toUpperCase()}
            </span>
            <input
              type={compact ? "text" : "number"}
              inputMode="numeric"
              aria-label={channel.toUpperCase()}
              min={0}
              max={255}
              value={roundedRgb[channel]}
              onChange={(event) =>
                applyRgbChannel(channel, Number(event.target.value))
              }
              className={
                compact
                  ? "w-full min-w-0 rounded-lg border border-[#ECECEC] px-1 py-2 text-center text-[12px] leading-[normal] text-[#0A0A0A] outline-none focus:border-cpaGold"
                  : "mt-1 h-9 w-full rounded border border-gray-300 px-1.5 text-center text-[13px] text-gray-950 outline-none focus:border-cpaGold"
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
};

// Label-left / swatch-right color row. The swatch opens the full picker plus
// the theme quick palette (blue / light blue / black / white for the blue
// theme, following the Step 2 Default Content Theme).
const ColorSwatchRow = ({
  label,
  color,
  quickPalette,
  swatches,
  disabled,
  onChange,
}: {
  label: string;
  color: string;
  quickPalette: string[];
  swatches?: string[];
  disabled: boolean;
  onChange: (hex: string) => void;
}) => {
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isOpen = position !== null;
  const displayLabel = label.replace(/^Select /, "");
  const palette =
    swatches ||
    (/Background/i.test(displayLabel)
      ? ["#FFFFFF", "#EFF5FD", "#FDF8EE"]
      : /Text|Line/i.test(displayLabel)
        ? ["#0A0A0A", "#1878F2", "#727272"]
        : /Highlight|Button|Accent|Link/i.test(displayLabel)
          ? ["#1878F2", "#E1A731", "#10B981", "#0A0A0A"]
          : quickPalette);
  useLayoutEffect(() => {
    if (!isOpen) return;
    const movePopup = () => {
      const anchor = triggerRef.current?.getBoundingClientRect();
      const popup = popupRef.current?.getBoundingClientRect();
      if (!anchor || !popup) return;
      const maxTop = Math.max(12, window.innerHeight - popup.height - 12);
      const below = anchor.bottom + 8;
      const above = anchor.top - popup.height - 8;
      const next = {
        left: Math.max(
          12,
          Math.min(
            anchor.right - popup.width,
            window.innerWidth - popup.width - 12,
          ),
        ),
        top: Math.max(12, Math.min(below <= maxTop ? below : above, maxTop)),
      };
      setPosition((current) =>
        current?.left === next.left && current.top === next.top
          ? current
          : next,
      );
    };
    movePopup();
    window.addEventListener("resize", movePopup);
    window.addEventListener("scroll", movePopup, true);
    return () => {
      window.removeEventListener("resize", movePopup);
      window.removeEventListener("scroll", movePopup, true);
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event: MouseEvent) => {
      if (
        !containerRef.current?.contains(event.target as Node) &&
        !popupRef.current?.contains(event.target as Node)
      )
        setPosition(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPosition(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [isOpen]);
  return (
    <div className="relative" ref={containerRef}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={colorRowLabelClassName}>{displayLabel}</span>
        <div className="flex items-center gap-1.5">
          {palette.map((hex) => (
            <button
              key={hex}
              type="button"
              disabled={disabled}
              title={hex}
              aria-label={`${displayLabel} ${hex}`}
              aria-pressed={color.toUpperCase() === hex}
              onClick={() => onChange(hex)}
              className={`h-[22px] w-[22px] rounded-md border-2 ${color.toUpperCase() === hex ? "border-[#0A0A0A]" : "border-[#ECECEC]"}`}
              style={{ backgroundColor: hex }}
            />
          ))}
          <button
            type="button"
            disabled={disabled}
            title={`Custom ${displayLabel}`}
            ref={triggerRef}
            aria-label={label}
            aria-expanded={!!position}
            className="h-[22px] w-[22px] rounded-md border-2 border-[#ECECEC]"
            style={{
              background:
                "conic-gradient(#F21B1B,#E1A731,#10B981,#1878F2,#9751F2,#F21B1B)",
            }}
            onClick={(event) => {
              if (position) {
                setPosition(null);
                return;
              }
              const bounds = event.currentTarget.getBoundingClientRect();
              setPosition({
                left: Math.max(
                  12,
                  Math.min(bounds.right - 232, window.innerWidth - 244),
                ),
                top: Math.max(
                  12,
                  Math.min(bounds.bottom + 8, window.innerHeight - 360),
                ),
              });
            }}
          />
        </div>
      </div>
      {position
        ? createPortal(
            <div
              ref={popupRef}
              role="dialog"
              aria-label={`${displayLabel} picker`}
              className="cmp-email-design fixed z-[10000] w-[232px] max-w-[calc(100vw-24px)] rounded-[14px] border border-[#ECECEC] bg-white p-[14px] shadow-[0_12px_36px_rgba(0,0,0,.18)]"
              style={{ ...position, fontFamily: editorFontFamily }}
            >
              <BuilderColorPicker
                color={color}
                onChange={onChange}
                appearance="builder"
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Settings-panel field primitives
// ---------------------------------------------------------------------------

const BlockFieldContext = createContext<{
  block: EmailBuilderBlock;
  disabled: boolean;
  patch: (updates: Record<string, unknown>) => void;
} | null>(null);

const getDesignField = (
  block: EmailBuilderBlock,
  label: string,
): { label: string; key?: string } => {
  const fields: Record<string, [string, string]> = {
    TITLE: ["SECTION TITLE", "title"],
    "SECTION TITLE": ["SECTION TITLE", "title"],
    CAPTION: ["CAPTION", "caption"],
    "HELPER TEXT": ["SUBTITLE", "helperText"],
    "BUTTON NAME": ["BUTTON LABEL", "buttonName"],
    "ALERT TITLE": ["SECTION TITLE", "alertTitle"],
    DESCRIPTION: ["TEXT", "description"],
    "TEXT CONTENT": ["TEXT", "textContent"],
    OPENING: ["OPENING", "opening"],
    "ADD SIGNATURE": ["ADD SIGNATURE", "addSignature"],
    NAME: ["NAME", "name"],
    DESIGNATION: ["TITLE LINE", "designation"],
    "CHAMBER NAME": ["ORGANIZATION", "chamberName"],
    "EXTRA TEXT": ["ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK", "extraText"],
    "SIMPLE TEXT": [
      block.type === "button"
        ? "BUTTON LABEL"
        : block.type === "image"
          ? "TEXT BESIDE IMAGE (OPTIONAL)"
          : "TEXT",
      "text",
    ],
  };
  if (label === "ADD LINK") return { label: "BUTTON LINK (URL)" };
  if (label === "LINK TO (OPTIONAL)") return { label: "LINK (URL)" };
  if (block.type === "advocacyUpdateBanner" && label === "LABEL") {
    return { label: "TITLE", key: "label" };
  }
  if (block.type === "specialGuestSponsor" && label === "TEXT CONTENT") {
    return {
      label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
      key: "textContent",
    };
  }
  const field = fields[label];
  if (!field) return { label };
  return {
    label: field[0],
    key:
      field[1] in block.settings || field[1] === "extraText"
        ? field[1]
        : undefined,
  };
};

const SettingsField = ({
  label,
  displayLabel,
  visibilityKey,
  children,
}: {
  label?: string;
  displayLabel?: string;
  visibilityKey?: string;
  children: React.ReactNode;
}) => {
  const context = useContext(BlockFieldContext);
  const automaticField: { label?: string; key?: string } =
    label && context ? getDesignField(context.block, label) : { label };
  const field = {
    label: displayLabel ?? automaticField.label,
    key: visibilityKey ?? automaticField.key,
  };
  const hidden =
    !!field.key && !!context?.block.settings.hiddenFields?.includes(field.key);
  return (
    <div className="cmp-email-builder-field">
      {label ? (
        <div className="flex items-center justify-between gap-2">
          <span className={settingLabelClassName}>{field.label}</span>
          {field.key && context ? (
            <button
              type="button"
              disabled={context.disabled}
              aria-label={`${hidden ? "Show" : "Hide"} ${field.label}`}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#727272] hover:text-[#0A0A0A]"
              onClick={() => {
                const current = context.block.settings.hiddenFields || [];
                context.patch({
                  hiddenFields: hidden
                    ? current.filter((key) => key !== field.key)
                    : [...current, field.key],
                });
              }}
            >
              {hidden ? (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 12 }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 12 }} />
              )}
              {hidden ? "Show" : "Hide"}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className={label ? "mt-1.5" : ""}>{children}</div>
    </div>
  );
};

const SettingsTextInput = ({
  label,
  ariaLabel,
  displayLabel,
  visibilityKey,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  label?: string;
  ariaLabel?: string;
  displayLabel?: string;
  visibilityKey?: string;
  value: string;
  placeholder?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) => {
  if (isEmailRichText(value)) {
    return <RichTextField label={label} displayLabel={displayLabel || ariaLabel} visibilityKey={visibilityKey} value={value} disabled={disabled} onChange={onChange} />;
  }
  const { text, marks } = getRichTextParts(value);
  return (
    <SettingsField
      label={label}
      displayLabel={displayLabel}
      visibilityKey={visibilityKey}
    >
      <input
        aria-label={ariaLabel || displayLabel || label}
        value={text}
        onChange={(event) =>
          onChange(buildRichTextValue(event.target.value, marks))
        }
        className={builderInputClassName}
        disabled={disabled}
        placeholder={placeholder}
      />
    </SettingsField>
  );
};

type TextAlignOption = "left" | "center" | "right" | "justify";

const RichTextField = ({
  label, displayLabel, visibilityKey, value, disabled,
  defaultTextAlign = "left", textAlign, onChange,
}: {
  label?: string;
  displayLabel?: string;
  visibilityKey?: string;
  value: string;
  disabled: boolean;
  defaultTextAlign?: TextAlignOption;
  textAlign?: TextAlignOption;
  onChange: (value: string) => void;
}) => (
  <SettingsField label={label} displayLabel={displayLabel} visibilityKey={visibilityKey}>
    <RichTextEditor
      variant="emailBuilder"
      value={richTextToHtml(value)}
      onChange={(html) => onChange(buildEmailRichTextValue(html))}
      readOnly={disabled}
      ariaLabel={displayLabel || label || "Block text"}
      defaultTextAlign={textAlign || defaultTextAlign}
      minHeight={96}
    />
  </SettingsField>
);

// Image upload/preview/replace/delete via the presigned-upload flow
// (EMAIL-CREATE-012 — uploadImage prop threads from EmailForm).
// Design (16) EB_IMG_REC: recommended size per image slot (help text and
// the "may look blurry" check).
const recommendedImageSizeByLabel: Record<string, readonly [number, number]> = {
  "SPONSOR LOGO": [480, 240],
  "BANNER IMAGE": [600, 200],
  "THUMBNAIL IMAGE": [1280, 720],
  "ADD IMAGE": [600, 600],
  LOGO: [320, 120],
  "BACKGROUND IMAGE": [1200, 600],
};

const ImageUploadControl = ({
  label,
  imageUrl,
  buttonLabel = "Replace Image",
  softRemove = label === "SPONSOR LOGO" || label === "BANNER IMAGE",
  recommended = (label && recommendedImageSizeByLabel[label]) || [1200, 800],
  uploadButtonLabel,
  removeButtonLabel,
  sideBySideActions = false,
  disabled,
  uploadImage,
  onChange,
  onRemove,
}: {
  label?: string;
  imageUrl: string;
  buttonLabel?: string;
  softRemove?: boolean;
  recommended?: readonly [number, number];
  uploadButtonLabel?: string;
  removeButtonLabel?: string;
  /** Design (19) Logo and Date: a contained preview, then Replace and Delete side by side. */
  sideBySideActions?: boolean;
  disabled: boolean;
  uploadImage: (file: File) => Promise<string>;
  onChange: (url: string) => void;
  /** Defaults to onChange(""). */
  onRemove?: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    const fileTypeError = getEmailImageFileError({ file });
    if (fileTypeError) {
      setError(fileTypeError);
      return;
    }
    setWarning("");
    // Design (16) ebPickImage: warn when the image is narrower than the
    // recommended width. Larger files are still reduced automatically.
    if (typeof URL.createObjectURL === "function") {
      const objectUrl = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        if (probe.naturalWidth < recommended[0])
          setWarning(`This image is ${probe.naturalWidth} × ${probe.naturalHeight} px and may look blurry. Use at least ${recommended[0]} px wide for best results.`);
        URL.revokeObjectURL(objectUrl);
      };
      probe.onerror = () => URL.revokeObjectURL(objectUrl);
      probe.src = objectUrl;
    }
    setError("");
    setIsUploading(true);
    try {
      onChange(await uploadImage(file));
    } catch (uploadError) {
      setError(
        getUserFacingErrorText(
          uploadError,
          buildStaffErrorFallback(
            "upload this image",
            "Check your internet connection.",
          ),
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const uploadLabel = uploadButtonLabel ||
    (label === "BACKGROUND IMAGE"
      ? "Click to upload a background photo"
      : label === "SPONSOR LOGO"
        ? "Click to upload the sponsor logo"
        : label === "BANNER IMAGE"
          ? "Click to upload a banner image"
          : label === "THUMBNAIL IMAGE"
            ? "Upload Thumbnail Image"
            : "Click to upload an image");
  const replaceLabel =
    label === "BACKGROUND IMAGE"
      ? "Replace Background Photo"
      : label === "SPONSOR LOGO"
        ? "Replace Logo"
        : label === "BANNER IMAGE"
          ? "Replace Banner Image"
          : buttonLabel;
  const removeLabel = removeButtonLabel ||
    (label === "BACKGROUND IMAGE"
      ? "Remove Background Photo"
      : label === "SPONSOR LOGO"
        ? "Remove Logo"
        : "Remove Image");
  const remove = onRemove || (() => onChange(""));
  return (
    <SettingsField label={label}>
      {sideBySideActions && imageUrl ? (
        <div className="flex flex-col gap-2">
          <img
            src={imageUrl}
            alt=""
            className="h-[90px] w-full rounded-lg border border-[#ECECEC] bg-white object-contain"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="flex min-w-0 items-center justify-center rounded-lg border border-[#9E9E9E] bg-white px-2.5 py-[9px] text-[13px] font-semibold text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || isUploading}
            >
              {isUploading ? <CircularProgress color="inherit" size={14} /> : replaceLabel}
            </button>
            <button
              type="button"
              className="flex min-w-0 items-center justify-center rounded-lg bg-[#F21B1B] px-2.5 py-[9px] text-[13px] font-semibold text-white hover:bg-[#D01414] disabled:cursor-not-allowed disabled:opacity-60"
              onClick={remove}
              disabled={disabled || isUploading}
            >
              {removeLabel}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              className="h-[110px] w-full rounded-lg border border-[#ECECEC] bg-white object-cover"
            />
          ) : null}
          <button
            type="button"
            className={
              imageUrl
                ? "flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#9E9E9E] bg-white px-[14px] py-2.5 text-[13px] font-semibold text-[#0A0A0A] transition hover:bg-[#F7F7F8] disabled:cursor-not-allowed disabled:opacity-60"
                : "flex w-full flex-col items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-[#D8D8DC] bg-[#FAFAFB] p-[18px] text-[12px] text-[#727272] disabled:cursor-not-allowed disabled:opacity-60"
            }
            onClick={() => inputRef.current?.click()}
            disabled={disabled || isUploading}
          >
            {isUploading ? (
              <CircularProgress color="inherit" size={14} />
            ) : imageUrl ? null : (
              <img
                src={getEmailBuilderIconDataUrl("image", "#9E9E9E")}
                width={24}
                height={24}
                alt=""
              />
            )}
            {imageUrl ? replaceLabel : uploadLabel}
          </button>
          {imageUrl ? (
            <button
              type="button"
              className={`inline-flex w-full items-center justify-center gap-1 rounded-lg px-[14px] py-2.5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${softRemove ? "bg-[#FDECEC] text-[#F21B1B] hover:bg-[#FADCDC]" : "bg-[#F21B1B] text-white hover:bg-[#D01414]"}`}
              title={removeLabel}
              aria-label={removeLabel}
              onClick={remove}
              disabled={disabled || isUploading}
            >
              {removeLabel}
            </button>
          ) : null}
        </div>
      )}
      <p className="mt-2 text-[12px] leading-[1.4] text-[#727272]">
        Recommended {recommended[0]} × {recommended[1]} px. JPG or PNG, max 1 MB.
      </p>
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      ) : null}
      {warning && imageUrl ? (
        <p role="status" className="mt-2 text-[12px] leading-[1.5] text-[#B7791F]">
          {warning}
        </p>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={emailImageAccept}
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          void handleFile(file);
        }}
      />
    </SettingsField>
  );
};

// Icon tile + gold "Change Icon" button opening a simple icon picker.
const IconPickerControl = ({
  label,
  icon,
  disabled,
  tileClassName = "border-[1.5px] border-[#1878F2] bg-white text-[#1878F2]",
  tileBackground,
  iconColor = "#1878F2",
  iconSize = 16,
  onChange,
}: {
  label?: string;
  icon: EmailBuilderIconKey;
  disabled: boolean;
  tileClassName?: string;
  /** A filled tile, such as the Urgent Alert Banner colour. */
  tileBackground?: string;
  iconColor?: string;
  iconSize?: number;
  onChange: (icon: EmailBuilderIconKey) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const popupRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const restoreTriggerFocusRef = useRef(false);

  useEffect(() => {
    if (!open && restoreTriggerFocusRef.current) {
      restoreTriggerFocusRef.current = false;
      buttonRef.current?.focus();
    }
  }, [open]);

  const closeAndRestoreTriggerFocus = () => {
    restoreTriggerFocusRef.current = true;
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const positionPopup = () => {
      const button = buttonRef.current;
      const popup = popupRef.current;
      if (!button || !popup) return;
      const anchor = button.getBoundingClientRect();
      const bounds = popup.getBoundingClientRect();
      const margin = 12;
      const maxLeft = Math.max(
        margin,
        window.innerWidth - bounds.width - margin,
      );
      const maxTop = Math.max(
        margin,
        window.innerHeight - bounds.height - margin,
      );
      const below = anchor.bottom + 8;
      const above = anchor.top - bounds.height - 8;
      const preferredTop =
        below <= maxTop ? below : above >= margin ? above : below;
      const next = {
        left: Math.max(margin, Math.min(anchor.left, maxLeft)),
        top: Math.max(margin, Math.min(preferredTop, maxTop)),
      };
      setPosition((current) =>
        current.left === next.left && current.top === next.top ? current : next,
      );
    };
    positionPopup();
    window.addEventListener("resize", positionPopup);
    window.addEventListener("scroll", positionPopup, true);
    return () => {
      window.removeEventListener("resize", positionPopup);
      window.removeEventListener("scroll", positionPopup, true);
    };
  }, [open]);

  // Shared dropdown behavior (SH-005, B11): close on outside click and Escape
  // like the module's other anchored menus (FilterDropdown pattern).
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !popupRef.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        restoreTriggerFocusRef.current = true;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <SettingsField label={label}>
      <div className="relative flex items-center gap-2.5" ref={containerRef}>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] ${tileClassName}`}
          style={tileBackground ? { background: tileBackground } : undefined}
        >
          <img
            src={getEmailBuilderIconDataUrl(icon, iconColor)}
            alt=""
            width={iconSize}
            height={iconSize}
          />
        </span>
        <button
          type="button"
          ref={buttonRef}
          className="rounded-md bg-[#E1A731] px-3 py-[7px] text-[11px] font-bold leading-[normal] text-white"
          onClick={() => setOpen((currentOpen) => !currentOpen)}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
        >
          Change Icon
        </button>
        {open
          ? createPortal(
              <div
                ref={popupRef}
                role="dialog"
                aria-label="Choose icon"
                style={position}
                className="cmp-email-design fixed z-[10000] grid max-h-[256px] w-[278px] max-w-[calc(100vw-24px)] grid-cols-5 gap-2 overflow-y-auto rounded-[14px] border border-[#ECECEC] bg-white p-3 shadow-[0_12px_36px_rgba(0,0,0,.16)]"
              >
                {getEmailBuilderIconOptions(icon).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    title={option.label}
                    aria-label={option.label}
                    onClick={() => {
                      onChange(option.key);
                      closeAndRestoreTriggerFocus();
                    }}
                    className={`flex h-11 w-11 items-center justify-center rounded-[10px] border-[1.5px] transition hover:border-cpaGold ${
                      option.key === icon
                        ? "border-cpaGold bg-[#FDF8EE] text-cpaGold"
                        : "border-gray-200 text-gray-700"
                    }`}
                  >
                    <img
                      src={getEmailBuilderIconDataUrl(
                        option.key,
                        option.key === icon ? "#E1A731" : "#0A0A0A",
                      )}
                      alt=""
                      width={19}
                      height={19}
                    />
                  </button>
                ))}
              </div>,
              document.body,
            )
          : null}
      </div>
    </SettingsField>
  );
};

// Numbered repeat-item card with the red trash delete (confirmed through the
// shared "Delete Item?" modal).
const GridItemCard = ({
  label,
  disabled,
  headerExtra,
  preserveFieldContext = false,
  separated = false,
  onDelete,
  children,
}: {
  label: string;
  disabled: boolean;
  headerExtra?: React.ReactNode;
  preserveFieldContext?: boolean;
  separated?: boolean;
  onDelete?: () => void;
  children: React.ReactNode;
}) => (
  <div
    className={
      separated
        ? "space-y-2.5 border-b border-[#ECECEC] pb-[14px]"
        : "space-y-2.5 rounded-[10px] border border-[#ECECEC] bg-white p-3"
    }
  >
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-[12px] font-bold uppercase tracking-[.05em] text-[#0A0A0A]">
        {label}
      </p>
      <div className="flex items-center gap-2">
        {headerExtra}
        {onDelete ? (
          <button
            type="button"
            className={
              separated
                ? "flex h-[26px] w-[26px] items-center justify-center rounded-md bg-[#F21B1B] text-white hover:bg-[#D01414] disabled:opacity-60 [&_.MuiSvgIcon-root]:text-[13px]"
                : itemTrashButtonClassName
            }
            title={`Delete ${label.toLowerCase()}`}
            aria-label={`Delete ${label.toLowerCase()}`}
            onClick={onDelete}
            disabled={disabled}
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        ) : null}
      </div>
    </div>
    {preserveFieldContext ? (
      children
    ) : (
      <BlockFieldContext.Provider value={null}>
        {children}
      </BlockFieldContext.Provider>
    )}
  </div>
);

const GridItemsHeader = ({
  title = "Grid Items",
  collapsible,
  collapsed,
  onToggle,
}: {
  title?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) =>
  collapsible ? (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between text-left"
    >
      <span className="text-[13px] font-bold text-gray-950">{title}</span>
      {collapsed ? (
        <KeyboardArrowDownIcon className="text-gray-500" />
      ) : (
        <KeyboardArrowUpIcon className="text-gray-500" />
      )}
    </button>
  ) : (
    <p className="text-[13px] font-bold text-gray-950">{title}</p>
  );

// BINDING "Delete Block" annotation: red circular trash badge, "Delete Item?"
// heading, red DELETE + dark CANCEL, red circular X close. Used for block
// deletes and repeat-item deletes — no silent deletes.
const DeleteConfirmModal = ({
  open,
  message,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  message?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const titleId = useId();
  const trashIcon = (size: number) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
  return (
    <ModalShell
      enableKeyboardFocus
      open={open}
      onClose={onCancel}
      showCloseButton={false}
      labelledById={titleId}
      panelClassName="cmp-email-design relative max-w-[440px] !rounded-[14px] !border-[#E5E7EB] !px-7 !py-[30px] !shadow-[0_18px_48px_rgba(0,0,0,.18)]"
      bodyClassName="text-center"
    >
      <div style={{ fontFamily: editorFontFamily }}>
        <h3 id={titleId} className="sr-only">
          Delete Item?
        </h3>
        <button
          type="button"
          aria-label="Close delete confirmation"
          onClick={onCancel}
          className="absolute right-[14px] top-[14px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white hover:bg-[#D01414]"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F21B1B] text-white">
          {trashIcon(29.44)}
        </span>
        <p className="mx-auto mt-[18px] max-w-[340px] text-pretty text-[14px] font-normal leading-[1.55] text-[#0A0A0A]">
          {message || "Delete this item from the email?"}
        </p>
        <div className="mt-[22px] flex w-full justify-center gap-3">
          <button
            type="button"
            className="flex max-w-[170px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#F21B1B] px-5 py-3 text-[14px] font-semibold text-white hover:bg-[#D01414]"
            onClick={onConfirm}
          >
            {trashIcon(14)}
            Delete
          </button>
          <button
            type="button"
            className="max-w-[170px] flex-1 rounded-lg bg-[#1F2124] px-5 py-3 text-[14px] font-semibold text-white hover:bg-[#3A3D42]"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// Real-event picker (EMAIL-009 — BINDING annotations on the Upcoming Events
// composite). Lists Live + public Events-module events.
// ---------------------------------------------------------------------------

// All event date/time labels render in the CHAMBER's time zone (the same
// convention the Events module and public website use), never the browser's.
const formatEventChip = (
  utcMilli: number,
  timeZone?: string,
): { month: string; day: string } => {
  const date = new Date(utcMilli);
  return {
    month: date
      .toLocaleString("en-US", { month: "short", timeZone })
      .toUpperCase(),
    day: date.toLocaleString("en-US", { day: "numeric", timeZone }),
  };
};

const formatEventTime = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

const formatEventTimeRange = (
  startUtcMilli: number,
  endUtcMilli: number,
  timeZone?: string,
): string =>
  `${formatEventTime(startUtcMilli, timeZone)} – ${formatEventTime(endUtcMilli, timeZone)}`;

const formatEventDateShort = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });

const formatEventDateLong = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone,
  });

// Row shape returned by the shared getCmpManageEvents query document.
type EventPickerRow =
  GetCmpManageEventsQuery["getCmpManageEvents"]["events"][number] & {
    description?: string;
    featuredImageUrl?: string;
  };

const EventPickerModal = ({
  mode,
  chamberId,
  initialSelectedEventId,
  addedEventIds = [],
  disabled,
  onClose,
  onConfirm,
}: {
  mode: "add" | "change";
  chamberId: string;
  initialSelectedEventId?: string;
  addedEventIds?: string[];
  disabled: boolean;
  onClose: () => void;
  onConfirm: (events: EventPickerRow[]) => void;
}) => {
  const [confirmationError, setConfirmationError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const timeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings?.setup?.timeZone,
  ).timeZone;
  const { data, loading, error, refetch } = useGetCmpManageEventsQuery({
    variables: {
      input: {
        chamberId,
        statuses: [CmpEventStatusEnum.Live, CmpEventStatusEnum.Published],
      },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const [getDetail] = useGetCmpEmailBuilderEventDetailLazyQuery({
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const now = Date.now();
  const available = (data?.getCmpManageEvents.events || [])
    .filter(
      (event) =>
        event.visibility === CmpEventVisibilityEnum.Public &&
        (!event.publishAtUtcMilli || event.publishAtUtcMilli <= now) &&
        event.endAtUtcMilli >= now &&
        (!addedEventIds.includes(event.id) ||
          event.id === initialSelectedEventId),
    )
    .sort((a, b) => a.startAtUtcMilli - b.startAtUtcMilli);
  return (
    <SourcePickerModal
      kind="events"
      items={available.map((event) => ({
        id: event.id,
        title: event.title,
        date: event.startAtUtcMilli,
        dateChip: formatEventChip(event.startAtUtcMilli, timeZone),
        meta: [
          event.locationLabel,
          formatEventTime(event.startAtUtcMilli, timeZone),
        ]
          .filter(Boolean)
          .join(" · "),
      }))}
      loading={loading || confirming}
      error={
        confirmationError ||
        (error
          ? getUserFacingErrorText(
              error,
              buildStaffErrorFallback("load the events"),
            )
          : "")
      }
      reload={() => {
        setConfirmationError("");
        void refetch();
      }}
      disabled={disabled || confirming}
      onClose={onClose}
      single={mode === "change"}
      initialSelectedId={initialSelectedEventId}
      onConfirm={(ids) => {
        setConfirming(true);
        setConfirmationError("");
        void Promise.all(
          available
            .filter((event) => ids.includes(event.id))
            .map(async (event) => {
              const result = await getDetail({
                variables: { input: { chamberId, slug: event.slug } },
                fetchPolicy: "network-only",
              });
              const detail = result.data?.getCmpPublicEventDetail.event;
              if (!detail || result.error)
                throw new UserFacingError(
                  `${event.title || "An event"} is no longer open to the public, so it cannot be added. Select Try again to load the current list.`,
                );
              const html =
                detail.heroShortDescription || detail.description || "";
              // Line breaks between saved paragraphs are not text. The helper counts
              // a blank paragraph as two blank lines; keep one.
              const description = emailRichTextToPlainText(
                buildEmailRichTextValue(html.replace(/(<\/(?:p|div|li|ul|ol)>)\s+/g, "$1")),
              ).replace(/\n{3,}/g, "\n\n");
              return {
                ...event,
                description,
                featuredImageUrl: detail.featuredImageUrl || "",
              };
            }),
        )
          .then(onConfirm)
          .catch((error) =>
            setConfirmationError(
              getUserFacingErrorText(
                error,
                buildStaffErrorFallback("add the selected events"),
              ),
            ),
          )
          .finally(() => setConfirming(false));
      }}
    />
  );
};

const EventItemsEditor = ({
  blockType,
  events,
  disabled,
  chamberId,
  chamberWebsiteUrl,
  requestDelete,
  onChangeEvents,
}: {
  blockType: "upcomingEventsList" | "eventAnnouncement";
  events: EmailBuilderEventItem[];
  disabled: boolean;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
  requestDelete: BlockPanelDeps["requestDelete"];
  onChangeEvents: (events: EmailBuilderEventItem[]) => void;
}) => {
  const [picker, setPicker] = useState<
    { mode: "add" } | { mode: "change"; itemId: string } | null
  >(null);
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const timeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings?.setup?.timeZone,
  ).timeZone;
  const update = (id: string, patch: Partial<EmailBuilderEventItem>) =>
    onChangeEvents(
      events.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  const announcement = blockType === "eventAnnouncement";
  return (
    <div className="space-y-4">
      {!announcement ? (
        <button
          type="button"
          className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
          disabled={disabled}
          onClick={() => setPicker({ mode: "add" })}
        >
          + Add Events
        </button>
      ) : null}
      {events.map((item, index) =>
        announcement ? (
          <div className="space-y-4" key={item.id}>
            {index > 0 ? (
              <div className="flex items-center justify-between">
                <span className={settingLabelClassName}>EVENT {index + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove event ${index + 1}`}
                  disabled={disabled}
                  onClick={() =>
                    requestDelete(() =>
                      onChangeEvents(
                        events.filter((event) => event.id !== item.id),
                      ),
                    )
                  }
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: "#F21B1B" }} />
                </button>
              </div>
            ) : null}
            <SettingsTextInput
              label="DATE"
              visibilityKey={`events.${item.id}.date`}
              value={item.date}
              disabled={disabled}
              onChange={(date) => update(item.id, { date })}
            />
            <SettingsTextInput
              label="TIME"
              visibilityKey={`events.${item.id}.time`}
              value={item.time}
              disabled={disabled}
              onChange={(time) => update(item.id, { time })}
            />
            <SettingsTextInput
              label="VENUE"
              visibilityKey={`events.${item.id}.venue`}
              value={item.location.split("\n")[0] || ""}
              disabled={disabled}
              onChange={(venue) =>
                update(item.id, {
                  location: [venue, ...item.location.split("\n").slice(1)].join(
                    "\n",
                  ),
                })
              }
            />
            <SettingsTextInput
              label="ADDRESS"
              visibilityKey={`events.${item.id}.address`}
              value={item.location.split("\n").slice(1).join("\n")}
              disabled={disabled}
              onChange={(address) =>
                update(item.id, {
                  location: [item.location.split("\n")[0] || "", address].join(
                    "\n",
                  ),
                })
              }
            />
          </div>
        ) : (
          <div
            key={item.id}
            className="flex items-center justify-between gap-2 border-b border-[#ECECEC] pb-[14px]"
          >
            <span className="text-[12px] font-bold uppercase tracking-[.05em] leading-[1.4]">
              {item.eventTitle || `Event ${index + 1}`}
            </span>
            <button
              type="button"
              aria-label={`Remove ${item.eventTitle || `Event ${index + 1}`}`}
              disabled={disabled}
              onClick={() =>
                requestDelete(() =>
                  onChangeEvents(
                    events.filter((event) => event.id !== item.id),
                  ),
                )
              }
              className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-[#F21B1B] text-white hover:bg-[#D01414]"
            >
              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
            </button>
          </div>
        ),
      )}
      {picker ? (
        <EventPickerModal
          mode={picker.mode}
          chamberId={chamberId}
          disabled={disabled}
          addedEventIds={events.map((item) => item.eventId).filter(Boolean)}
          initialSelectedEventId={
            picker.mode === "change"
              ? events.find((item) => item.id === picker.itemId)?.eventId
              : undefined
          }
          onClose={() => setPicker(null)}
          onConfirm={(rows) => {
            const fields = (
              row: EventPickerRow,
            ): Omit<EmailBuilderEventItem, "id"> => ({
              eventId: row.id,
              eventTitle: row.title,
              date: announcement
                ? formatEventDateLong(row.startAtUtcMilli, timeZone)
                : formatEventDateShort(row.startAtUtcMilli, timeZone),
              time: announcement
                ? formatEventTimeRange(
                    row.startAtUtcMilli,
                    row.endAtUtcMilli,
                    timeZone,
                  )
                : formatEventTime(row.startAtUtcMilli, timeZone),
              location: row.locationLabel,
              linkUrl: buildEmailBuilderEventLinkUrl({
                chamberWebsiteUrl,
                slug: row.slug,
                destination: announcement ? "registration" : "details",
              }),
              tag: row.eventTypeLabel || "",
              tagColor: row.eventTypeColor || "",
              description: row.description || "",
              imageUrl: row.featuredImageUrl || "",
            });
            if (picker.mode === "change" && rows[0])
              update(picker.itemId, fields(rows[0]));
            else
              onChangeEvents([
                ...events,
                ...rows.map((row) => createEventItem(fields(row))),
              ]);
            setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-block settings panels (field names, order, labels, placeholders and
// helper copy transcribed verbatim from blocks-a.md / blocks-b.md)
// ---------------------------------------------------------------------------

type BlockPanelDeps = {
  disabled: boolean;
  quickPalette: string[];
  memberOptions?: EmailAudienceMemberOption[];
  memberOptionsLoading?: boolean;
  newsroomSource: NewsroomSource;
  uploadImage: (file: File) => Promise<string>;
  uploadPdf: (file: File) => Promise<string>;
  requestDelete: (onConfirm: () => void, message?: string) => void;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
  /** Step 2 "Chamber Logo" (Logo and Date block). */
  campaignLogoUrl?: string;
  /** Footer block (EC-33): the email-wide footer message and colour. */
  footerText?: string;
  footerBackgroundColor?: string;
  onFooterChange?: (updates: {
    footerText?: string;
    footerBackgroundColor?: string;
  }) => void;
};

type BlockPanelProps<T extends EmailBuilderBlockType> = BlockPanelDeps & {
  block: EmailBuilderBlockOf<T>;
  onUpdate: (updates: Partial<EmailBuilderBlockSettingsMap[T]>) => void;
};

const addLinkPlaceholder = "https://...";
const linkToOptionalPlaceholder = "https://...";

// Two-option segmented control (design (16) IMAGE POSITION / IMAGE DISPLAY).
const BuilderSegmented = <T extends string>({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; title?: string }>;
  disabled: boolean;
  onChange: (value: T) => void;
}) => (
  <SettingsField label={label}>
    <div
      role="group"
      aria-label={label}
      className="grid grid-cols-2 gap-1 rounded-[9px] border border-[#9E9E9E] p-[3px]"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title}
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={`rounded-[6px] px-[10px] py-2 text-[13px] font-semibold ${
            value === option.value ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </SettingsField>
);

// Design (16) hero (EC-25): TITLE, SUBTITLE, ADD IMAGE (position, display,
// width) and Background Colour; no button. Saved heroes keep their button
// and background photo controls while those hold content.
const TextAndButtonHeroBannerPanel = ({
  block,
  disabled,
  uploadImage,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"textAndButtonHeroBanner">) => {
  const s = block.settings;
  const hasButton = !!s.buttonName.trim();
  return (
    <div className="space-y-4">
      <BuilderFields
        block={block}
        disabled={disabled}
        onUpdate={onUpdate}
        fields={[
          { key: "title", label: "SECTION TITLE" },
          { key: "helperText", label: "SUBTITLE" },
          ...(hasButton ? [{ key: "buttonName", label: "BUTTON LABEL" }] : []),
          {
            key: "extraText",
            label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
            area: true,
          },
        ]}
      />
      {hasButton ? (
        <>
          <ColorSwatchRow
            label="Button Colour"
            color={s.highlightColor}
            quickPalette={[]}
            disabled={disabled}
            onChange={(highlightColor) => onUpdate({ highlightColor })}
          />
          <ButtonTargetControl
            block={block}
            disabled={disabled}
            uploadPdf={uploadPdf}
            requestDelete={requestDelete}
            onUpdate={onUpdate}
          />
        </>
      ) : null}
      <ImageUploadControl
        label="ADD IMAGE"
        imageUrl={s.sideImageUrl || ""}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(sideImageUrl) => onUpdate({ sideImageUrl })}
      />
      {s.sideImageUrl ? (
        <>
          <BuilderSegmented
            label="IMAGE POSITION"
            value={s.sideImagePosition === "right" ? "right" : "left"}
            options={[
              { value: "left", label: "Image Left" },
              { value: "right", label: "Image Right" },
            ]}
            disabled={disabled}
            onChange={(sideImagePosition) => onUpdate({ sideImagePosition })}
          />
          <BuilderSegmented
            label="IMAGE DISPLAY"
            value={s.sideImageFit === "contain" ? "contain" : "cover"}
            options={[
              { value: "cover", label: "Cover Image", title: "Fill the frame; edges may be cropped" },
              { value: "contain", label: "Fit Image", title: "Show the whole image without cropping" },
            ]}
            disabled={disabled}
            onChange={(sideImageFit) => onUpdate({ sideImageFit })}
          />
          <p className="-mt-2 text-[12px] leading-[1.4] text-[#727272]">
            Cover fills the frame and may crop the edges. Fit shows the whole
            image.
          </p>
          <BuilderRange
            label="IMAGE WIDTH"
            value={s.sideImageWidth || 200}
            min={90}
            max={300}
            step={10}
            unit="px"
            disabled={disabled}
            onChange={(sideImageWidth) => onUpdate({ sideImageWidth })}
          />
        </>
      ) : null}
      {s.backgroundImageUrl ? (
        <ImageUploadControl
          label="BACKGROUND IMAGE"
          imageUrl={s.backgroundImageUrl}
          disabled={disabled}
          uploadImage={uploadImage}
          onChange={(backgroundImageUrl) => onUpdate({ backgroundImageUrl })}
        />
      ) : null}
      <ColorSwatchRow
        label="Background Colour"
        color={s.backgroundColor}
        quickPalette={[]}
        swatches={["#14161A", "#1878F2", "#E1A731", "#10B981", "#F7F7F8"]}
        disabled={disabled}
        onChange={(backgroundColor) =>
          onUpdate({
            backgroundColor,
            textColor: getReadableEmailTextColor(backgroundColor),
          })
        }
      />
    </div>
  );
};

const TextHeroBannerPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"textHeroBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<TextHeroBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        onChange={(value) => patch({ textContent: value })}
      />
    </div>
  );
};

const EmailBannerPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"emailBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<EmailBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Colour"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <ImageUploadControl
        label="CEO IMAGE"
        imageUrl={s.ceoImageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ ceoImageUrl: url })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Text Colour"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsTextInput
        label="ADD LINK"
        value={s.addLink}
        placeholder={addLinkPlaceholder}
        disabled={disabled}
        onChange={(value) => patch({ addLink: value })}
      />
    </div>
  );
};

const UpdateBannerPanel = ({
  block,
  disabled,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"updateBanner">) => (
  <div className="space-y-4">
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "title", label: "SECTION TITLE" },
        { key: "description", label: "TEXT", area: true },
        { key: "buttonName", label: "BUTTON LABEL" },
      ]}
    />
    <ColorSwatchRow
      label="Button Colour"
      color={block.settings.highlightColor}
      quickPalette={[]}
      disabled={disabled}
      onChange={(highlightColor) => onUpdate({ highlightColor })}
    />
    <ButtonTargetControl
      block={block}
      disabled={disabled}
      uploadPdf={uploadPdf}
      requestDelete={requestDelete}
      onUpdate={onUpdate}
    />
  </div>
);

const AlertBannerPanel = ({
  block,
  disabled,
  onUpdate,
}: BlockPanelProps<"alertBanner">) => (
  <div className="space-y-4">
    {/* Design (19) alert panel: ICON tile in the banner colour + Change Icon. */}
    <IconPickerControl
      label="ICON"
      icon={block.settings.alertIcon}
      disabled={disabled}
      tileClassName=""
      tileBackground={block.settings.backgroundColor}
      iconColor={block.settings.textColor || "#FFFFFF"}
      iconSize={17}
      onChange={(alertIcon) => onUpdate({ alertIcon })}
    />
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "alertTitle", label: "TITLE" },
        {
          key: "extraText",
          label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
          area: true,
        },
      ]}
    />
    <ColorSwatchRow
      label="Banner Colour"
      color={block.settings.backgroundColor}
      quickPalette={[]}
      swatches={["#1878F2", "#E1A731", "#F21B1B", "#0A0A0A"]}
      disabled={disabled}
      onChange={(backgroundColor) => onUpdate({ backgroundColor })}
    />
  </div>
);

const AdvocacyUpdateBannerPanel = ({
  block,
  disabled,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"advocacyUpdateBanner">) => (
  <div className="space-y-4">
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "title", label: "SECTION TITLE" },
        { key: "label", label: "TITLE" },
        { key: "description", label: "TEXT", area: true },
        { key: "buttonName", label: "BUTTON LABEL" },
      ]}
    />
    <ButtonTargetControl
      block={block}
      disabled={disabled}
      uploadPdf={uploadPdf}
      requestDelete={requestDelete}
      onUpdate={onUpdate}
    />
    {/* COLOR_DEFS advocacy (EC-34): Background Colour; the text follows. */}
    <ColorSwatchRow
      label="Background Colour"
      color={block.settings.backgroundColor}
      quickPalette={[]}
      swatches={["#0A0A0A", "#1878F2", "#E1A731", "#10B981", "#F7F7F8"]}
      disabled={disabled}
      onChange={(backgroundColor) =>
        onUpdate({
          backgroundColor,
          textColor: getReadableEmailTextColor(backgroundColor),
        })
      }
    />
  </div>
);

const MemberSpotlightPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"memberSpotlight">) => {
  const s = block.settings;
  const patch = (updates: Partial<MemberSpotlightSettings>) =>
    onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<MemberSpotlightSettings["items"][number]>,
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item,
      ),
    });
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Colour"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              patch({
                items: s.items.filter((current) => current.id !== item.id),
              }),
            )
          }
        >
          <ImageUploadControl
            imageUrl={item.imageUrl}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(url) => updateItem(item.id, { imageUrl: url })}
          />
          <RichTextField
            label="TITLE"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <RichTextField
            label="DESCRIPTION"
            value={item.description}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { description: value })}
          />
          <SettingsTextInput
            label="BUTTON NAME"
            value={item.buttonName}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { buttonName: value })}
          />
          <SettingsTextInput
            label="ADD LINK"
            value={item.addLink}
            placeholder={addLinkPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { addLink: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() =>
          patch({ items: [...s.items, createMemberSpotlightItem()] })
        }
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </div>
  );
};

const ArticleListPanel = ({
  block,
  disabled,
  requestDelete,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"articleList">) => {
  const s = block.settings;
  const updateItem = (
    id: string,
    updates: Partial<ArticleListSettings["items"][number]>,
  ) =>
    onUpdate({
      items: s.items.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      ),
    });
  return (
    <div className="space-y-4">
      <BuilderFields
        block={block}
        disabled={disabled}
        onUpdate={onUpdate}
        fields={[{ key: "title", label: "SECTION TITLE" }]}
      />
      <ColorSwatchRow
        label="Highlight Colour"
        color={s.highlightColor}
        quickPalette={[]}
        disabled={disabled}
        onChange={(highlightColor) => onUpdate({ highlightColor })}
      />
      <BuilderRange
        label="Image size"
        value={s.imageSize || 150}
        min={90}
        max={260}
        step={10}
        unit="px"
        disabled={disabled}
        onChange={(imageSize) => onUpdate({ imageSize })}
      />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`INFORMATION BLOCK ${index + 1}`}
          disabled={disabled}
          onDelete={
            s.items.length > 1
              ? () =>
                  requestDelete(
                    () =>
                      onUpdate({
                        items: s.items.filter(
                          (current) => current.id !== item.id,
                        ),
                      }),
                    `Are you sure you want to delete Information Block ${index + 1}?`,
                  )
              : undefined
          }
        >
          <SettingsTextInput
            label="HEADING"
            value={item.title}
            disabled={disabled}
            onChange={(title) => updateItem(item.id, { title })}
          />
          <SettingsField label="TEXT">
            <textarea
              aria-label="TEXT"
              rows={3}
              className={`${builderInputClassName} resize-y`}
              value={item.description}
              disabled={disabled}
              onChange={(event) =>
                updateItem(item.id, { description: event.target.value })
              }
            />
          </SettingsField>
          <SettingsTextInput
            label="LINK TEXT"
            value={item.linkText || ""}
            disabled={disabled}
            onChange={(linkText) => updateItem(item.id, { linkText })}
          />
          {item.linkText ? (
            <SettingsTextInput
              label="LINK (URL)"
              value={item.linkUrl || ""}
              placeholder="https://..."
              disabled={disabled}
              onChange={(linkUrl) => updateItem(item.id, { linkUrl })}
            />
          ) : null}
          <ImageUploadControl
            label="IMAGE"
            imageUrl={item.imageUrl || ""}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(imageUrl) => updateItem(item.id, { imageUrl })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        disabled={disabled}
        onClick={() =>
          onUpdate({ items: [...s.items, createArticleListItem()] })
        }
      >
        + Add
      </button>
    </div>
  );
};

const SplitArticleWithBorderPanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"splitArticleWithBorder">) => {
  const s = block.settings;
  const patch = (updates: Partial<SplitArticleWithBorderSettings>) =>
    onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<SplitArticleWithBorderSettings["items"][number]>,
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item,
      ),
    });
  return (
    <div className="space-y-4">
      <BuilderFields
        block={block}
        disabled={disabled}
        onUpdate={onUpdate}
        fields={[
          {
            key: "extraText",
            label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
            area: true,
          },
        ]}
      />
      <ColorSwatchRow
        label="Highlight Colour"
        color={s.highlightColor}
        quickPalette={[]}
        swatches={["#1878F2", "#E1A731", "#10B981"]}
        disabled={disabled}
        onChange={(highlightColor) => onUpdate({ highlightColor })}
      />
      {/* FIELD_DEFS split3 (EC-29): Articles, ARTICLE <n> with ICON,
          HEADING, TEXT, LINK and LINK URL, "+ Add Article". */}
      <p className="text-[13px] font-bold text-[#0A0A0A]">Articles</p>
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ARTICLE ${index + 1}`}
          disabled={disabled}
          onDelete={
            s.items.length > 1
              ? () =>
                  requestDelete(() =>
                    patch({
                      items: s.items.filter((current) => current.id !== item.id),
                    }),
                  )
              : undefined
          }
        >
          <IconPickerControl
            icon={item.icon}
            disabled={disabled}
            onChange={(icon) => updateItem(item.id, { icon })}
          />
          <RichTextField
            label={`ARTICLE ${index + 1} HEADING`}
            displayLabel="HEADING"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <RichTextField
            label={`ARTICLE ${index + 1} TEXT`}
            displayLabel="TEXT"
            value={item.desc}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { desc: value })}
          />
          <SettingsTextInput
            label={`ARTICLE ${index + 1} LINK`}
            ariaLabel={`ARTICLE ${index + 1} LINK`}
            displayLabel="LINK"
            value={item.buttonName}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { buttonName: value })}
          />
          <SettingsTextInput
            label={`ARTICLE ${index + 1} LINK URL`}
            ariaLabel={`ARTICLE ${index + 1} LINK URL`}
            displayLabel="LINK URL"
            value={item.addLink}
            placeholder={addLinkPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { addLink: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() =>
          patch({ items: [...s.items, createSplitArticleItem()] })
        }
        disabled={disabled}
      >
        + Add Article
      </button>
    </div>
  );
};

const TextAndEmailContentPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"textAndEmailContent">) => {
  const s = block.settings;
  const patch = (updates: Partial<TextAndEmailContentSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        textAlign={s.textAlign}
        onChange={(value) => patch({ textContent: value })}
      />
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
    </div>
  );
};

const IconGridItemsEditor = ({
  items,
  disabled,
  requestDelete,
  uploadPdf,
  onChangeItems,
}: {
  items: FourGridIconAndTitleSettings["items"];
  uploadPdf?: (file: File) => Promise<string>;
  disabled: boolean;
  requestDelete: (onConfirm: () => void, message?: string) => void;
  onChangeItems: (items: FourGridIconAndTitleSettings["items"]) => void;
}) => {
  const updateItem = (
    itemId: string,
    updates: Partial<FourGridIconAndTitleSettings["items"][number]>,
  ) =>
    onChangeItems(
      items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item,
      ),
    );
  return (
    <>
      {items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              onChangeItems(items.filter((current) => current.id !== item.id)),
            )
          }
        >
          <IconPickerControl
            icon={item.icon}
            disabled={disabled}
            onChange={(icon) => updateItem(item.id, { icon })}
          />
          <SettingsTextInput
            ariaLabel={`Item ${index + 1} label`}
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          {uploadPdf ? (
            <>
              <div className="flex gap-3 text-[12px] font-semibold">
                <label className="flex items-center gap-1.5">
                  <Radio
                    sx={inlineChoiceControlSx}
                    name={`grid-link-${item.id}`}
                    checked={item.linkMode !== "pdf"}
                    disabled={disabled}
                    onChange={() => updateItem(item.id, { linkMode: "url" })}
                  />{" "}
                  Link (URL)
                </label>
                <label className="flex items-center gap-1.5">
                  <Radio
                    sx={inlineChoiceControlSx}
                    name={`grid-link-${item.id}`}
                    checked={item.linkMode === "pdf"}
                    disabled={disabled}
                    onChange={() => updateItem(item.id, { linkMode: "pdf" })}
                  />{" "}
                  Attach PDF
                </label>
              </div>
              {item.linkMode === "pdf" ? (
                <PdfAttachmentControl
                  pdfUrl={item.pdfUrl || ""}
                  pdfFileName={item.pdfFileName || ""}
                  disabled={disabled}
                  uploadPdf={uploadPdf}
                  requestDelete={requestDelete}
                  onChange={(pdfUrl, pdfFileName) =>
                    updateItem(item.id, { pdfUrl, pdfFileName })
                  }
                />
              ) : (
                <SettingsTextInput
                  ariaLabel={`Item ${index + 1} link URL`}
                  value={item.linkTo}
                  placeholder="https://..."
                  disabled={disabled}
                  onChange={(linkTo) => updateItem(item.id, { linkTo })}
                />
              )}
            </>
          ) : (
            <SettingsTextInput
              label="LINK TO (OPTIONAL)"
              value={item.linkTo}
              placeholder="https://..."
              disabled={disabled}
              onChange={(linkTo) => updateItem(item.id, { linkTo })}
            />
          )}
        </GridItemCard>
      ))}
      <button
        type="button"
        className="w-full rounded-lg bg-[#E1A731] p-3 text-[13px] font-bold leading-[normal] text-white"
        onClick={() => onChangeItems([...items, createIconGridItem()])}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </>
  );
};

const FourGridIconAndTitlePanel = ({
  block,
  disabled,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"fourGridIconAndTitle">) => (
  <div className="space-y-4">
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "title", label: "SECTION TITLE", area: true, align: "center" },
        {
          key: "extraText",
          label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
          area: true,
        },
      ]}
    />
    <ColorSwatchRow
      label="Text Colour"
      color={block.settings.textColor}
      quickPalette={[]}
      swatches={["#0A0A0A", "#1E1B4B", "#727272"]}
      disabled={disabled}
      onChange={(textColor) => onUpdate({ textColor })}
    />
    <ColorSwatchRow
      label="Highlight Colour"
      color={block.settings.highlightColor}
      quickPalette={[]}
      disabled={disabled}
      onChange={(highlightColor) => onUpdate({ highlightColor })}
    />
    <ColorSwatchRow
      label="Background Colour"
      color={block.settings.backgroundColor}
      quickPalette={[]}
      disabled={disabled}
      onChange={(backgroundColor) => onUpdate({ backgroundColor })}
    />
    <p className="text-[13px] font-bold">Grid Items</p>
    <IconGridItemsEditor
      items={block.settings.items}
      disabled={disabled}
      uploadPdf={uploadPdf}
      requestDelete={requestDelete}
      onChangeItems={(items) => onUpdate({ items })}
    />
  </div>
);

const SixGridIconAndTitlePanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"sixGridIconAndTitle">) => {
  const s = block.settings;
  const patch = (updates: Partial<SixGridIconAndTitleSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Colour"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <GridItemsHeader />
      <IconGridItemsEditor
        items={s.items}
        disabled={disabled}
        requestDelete={requestDelete}
        onChangeItems={(items) => patch({ items })}
      />
    </div>
  );
};

const UpcomingEventsListPanel = ({
  block,
  disabled,
  requestDelete,
  chamberId,
  chamberWebsiteUrl,
  onUpdate,
}: BlockPanelProps<"upcomingEventsList">) => (
  <div className="space-y-4">
    <RichTextField
      label="SECTION TITLE"
      value={block.settings.title}
      disabled={disabled}
      onChange={(title) => onUpdate({ title })}
    />
    <EventItemsEditor
      blockType="upcomingEventsList"
      events={block.settings.events}
      disabled={disabled}
      chamberId={chamberId}
      chamberWebsiteUrl={chamberWebsiteUrl}
      requestDelete={requestDelete}
      onChangeEvents={(events) => onUpdate({ events })}
    />
    <ColorSwatchRow
      label="Button Colour"
      color={block.settings.buttonColor || block.settings.highlightColor}
      quickPalette={[]}
      swatches={["#1878F2", "#E1A731", "#10B981", "#0A0A0A", "#F21B1B"]}
      disabled={disabled}
      onChange={(buttonColor) => onUpdate({ buttonColor })}
    />
  </div>
);

const EventAnnouncementPanel = ({
  block,
  disabled,
  requestDelete,
  uploadPdf,
  chamberId,
  chamberWebsiteUrl,
  onUpdate,
}: BlockPanelProps<"eventAnnouncement">) => (
  <div className="space-y-4">
    <EventItemsEditor
      blockType="eventAnnouncement"
      events={
        block.settings.events.length
          ? block.settings.events
          : [
              {
                id: `${block.id}-details`,
                eventId: "",
                eventTitle: "",
                date: "",
                time: "",
                location: "",
                linkUrl: "",
              },
            ]
      }
      disabled={disabled}
      chamberId={chamberId}
      chamberWebsiteUrl={chamberWebsiteUrl}
      requestDelete={requestDelete}
      onChangeEvents={(events) => onUpdate({ events })}
    />
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "buttonName", label: "BUTTON LABEL" },
        {
          key: "extraText",
          label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
          area: true,
        },
      ]}
    />
    <ColorSwatchRow
      label="Highlight Colour"
      color={block.settings.highlightColor}
      quickPalette={[]}
      swatches={["#1878F2", "#E1A731", "#10B981"]}
      disabled={disabled}
      onChange={(highlightColor) => onUpdate({ highlightColor })}
    />
    <ColorSwatchRow
      label="Button Colour"
      color={block.settings.buttonColor || block.settings.highlightColor}
      quickPalette={[]}
      swatches={["#1878F2", "#E1A731", "#10B981", "#0A0A0A", "#F21B1B"]}
      disabled={disabled}
      onChange={(buttonColor) => onUpdate({ buttonColor })}
    />
    <ButtonTargetControl
      block={block}
      disabled={disabled}
      uploadPdf={uploadPdf}
      requestDelete={requestDelete}
      onUpdate={onUpdate}
    />
  </div>
);

const SpecialGuestSponsorPanel = ({
  block,
  disabled,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"specialGuestSponsor">) => (
  <div className="space-y-4">
    <BuilderFields
      block={block}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[
        { key: "title", label: "SECTION TITLE" },
        {
          key: "textContent",
          label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
          area: true,
        },
      ]}
    />
    <ImageUploadControl
      label="SPONSOR LOGO"
      imageUrl={block.settings.sponsorLogoUrl}
      disabled={disabled}
      uploadImage={uploadImage}
      onChange={(sponsorLogoUrl) => onUpdate({ sponsorLogoUrl })}
    />
  </div>
);

const SponsorsListPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"sponsorsList">) => {
  const s = block.settings;
  const patch = (updates: Partial<SponsorsListSettings>) => onUpdate(updates);
  const updateSponsor = (
    sponsorId: string,
    updates: Partial<SponsorsListSettings["sponsors"][number]>,
  ) =>
    patch({
      sponsors: s.sponsors.map((sponsor) =>
        sponsor.id === sponsorId ? { ...sponsor, ...updates } : sponsor,
      ),
    });
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader title="SPONSOR LOGO" />
      {s.sponsors.map((sponsor, index) => (
        <GridItemCard
          key={sponsor.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled || s.sponsors.length === 1}
          onDelete={() =>
            requestDelete(() =>
              patch({
                sponsors: s.sponsors.filter(
                  (current) => current.id !== sponsor.id,
                ),
              }),
            )
          }
        >
          <ImageUploadControl
            imageUrl={sponsor.logoUrl}
            buttonLabel="Change Sponsor Logo"
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(url) => updateSponsor(sponsor.id, { logoUrl: url })}
          />
          <SettingsTextInput
            label="LINK TO (OPTIONAL)"
            value={sponsor.linkTo}
            placeholder={linkToOptionalPlaceholder}
            disabled={disabled}
            onChange={(value) => updateSponsor(sponsor.id, { linkTo: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() =>
          patch({ sponsors: [...s.sponsors, createSponsorItem()] })
        }
        disabled={disabled || s.sponsors.length >= 3}
      >
        + Add Sponsor
      </button>
      {s.sponsors.length >= 3 ? (
        <p className="text-[13px] text-gray-500">
          This block shows a maximum of 3 sponsors. Remove a sponsor to add
          another.
        </p>
      ) : null}
    </div>
  );
};

const AdvertisingPdfPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"advertisingPdf">) => {
  const s = block.settings;
  const patch = (updates: Partial<AdvertisingPdfSettings>) => onUpdate(updates);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const handlePdfFile = async (file?: File | null) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      setPdfError("This file is not a PDF. Choose a PDF file.");
      return;
    }
    if (file.size > maxBuilderPdfBytes) {
      setPdfError("This PDF is larger than 10 MB. Choose a smaller PDF.");
      return;
    }
    setPdfError("");
    setIsUploadingPdf(true);
    try {
      const url = await uploadPdf(file);
      // The button destination is the link OR the PDF, never both.
      patch({ attachPdfUrl: url, attachPdfName: file.name, addLink: "" });
    } catch (uploadError) {
      setPdfError(
        getUserFacingErrorText(
          uploadError,
          buildStaffErrorFallback(
            "upload this PDF",
            "Check your internet connection.",
          ),
        ),
      );
    } finally {
      setIsUploadingPdf(false);
    }
  };

  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Colour"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ImageUploadControl
        label="IMAGE"
        imageUrl={s.imageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ imageUrl: url })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Text Colour"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsField label="ADD LINK">
        <input
          value={s.addLink}
          onChange={(event) =>
            patch({
              addLink: event.target.value,
              attachPdfUrl: "",
              attachPdfName: "",
            })
          }
          className={inputClassName}
          disabled={disabled || !!s.attachPdfUrl}
          placeholder={addLinkPlaceholder}
        />
        {s.attachPdfUrl ? (
          <p className="mt-1.5 text-[13px] text-gray-500">
            Remove the attached PDF to link the button to a web address instead.
          </p>
        ) : null}
      </SettingsField>
      <SettingsField label="OR ATTACH A PDF">
        {s.attachPdfUrl ? (
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600">
              <AttachFileIcon fontSize="small" />
            </span>
            <span className="min-w-0 flex-1 truncate text-[14px] text-gray-950">
              {s.attachPdfName || "Attached PDF"}
            </span>
            <button
              type="button"
              className={itemTrashButtonClassName}
              title="Remove PDF"
              aria-label="Remove PDF"
              onClick={() =>
                requestDelete(
                  () => patch({ attachPdfUrl: "", attachPdfName: "" }),
                  "Are you sure you want to remove the attached PDF?",
                )
              }
              disabled={disabled || isUploadingPdf}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={disabled || isUploadingPdf || !!s.addLink.trim()}
            title={
              s.addLink.trim()
                ? "Clear the button link to attach a PDF instead"
                : "Attach a PDF"
            }
            className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-[#9E9E9E] bg-white px-4 text-[13px] font-semibold disabled:opacity-60"
          >
            {isUploadingPdf ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              <AttachFileIcon />
            )}{" "}
            Attach PDF
          </button>
        )}
        {pdfError ? (
          <p className="mt-2 text-sm text-red-600">{pdfError}</p>
        ) : null}
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          disabled={disabled || isUploadingPdf}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            void handlePdfFile(file);
          }}
        />
      </SettingsField>
    </div>
  );
};

const KeyLinksPanel = ({
  block,
  disabled,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"keyLinks">) => {
  const updateItem = (itemId: string, updates: Partial<KeyLinkItem>) =>
    onUpdate({
      items: block.settings.items.map((current) =>
        current.id === itemId ? { ...current, ...updates } : current,
      ),
    });
  return (
    <div className="space-y-4">
      <BuilderFields
        block={block}
        disabled={disabled}
        onUpdate={onUpdate}
        fields={[{ key: "title", label: "SECTION TITLE" }]}
      />
      {/* D@389244 (EC-31): Links, LINK <n> cards with LINK NAME and LINK
          URL, no limit, "+ Add Another Link". */}
      <p className="text-[13px] font-bold text-[#0A0A0A]">Links</p>
      {block.settings.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`LINK ${index + 1}`}
          disabled={disabled}
          preserveFieldContext
          onDelete={
            block.settings.items.length > 1
              ? () =>
                  requestDelete(() =>
                    onUpdate({
                      items: block.settings.items.filter(
                        (current) => current.id !== item.id,
                      ),
                    }),
                  )
              : undefined
          }
        >
          <SettingsTextInput
            label={`LINK ${index + 1} NAME`}
            ariaLabel={`LINK ${index + 1} NAME`}
            displayLabel="LINK NAME"
            visibilityKey={`items.${item.id}.buttonText`}
            value={item.buttonText}
            disabled={disabled}
            onChange={(buttonText) => updateItem(item.id, { buttonText })}
          />
          <SettingsTextInput
            label={`LINK ${index + 1} URL`}
            ariaLabel={`LINK ${index + 1} URL`}
            displayLabel="LINK URL"
            value={item.addLink}
            placeholder="https://"
            disabled={disabled}
            onChange={(addLink) => updateItem(item.id, { addLink })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
        disabled={disabled}
        onClick={() =>
          onUpdate({ items: [...block.settings.items, createKeyLinkItem()] })
        }
      >
        + Add Another Link
      </button>
      <BuilderFields
        block={block}
        disabled={disabled}
        onUpdate={onUpdate}
        fields={[
          {
            key: "extraText",
            label: "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK",
            area: true,
          },
        ]}
      />
      <ColorSwatchRow
        label="Link Colour"
        color={block.settings.highlightColor}
        quickPalette={[]}
        swatches={["#1878F2", "#E1A731", "#10B981"]}
        disabled={disabled}
        onChange={(highlightColor) => onUpdate({ highlightColor })}
      />
    </div>
  );
};

const InquiriesPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"inquiries">) => {
  const s = block.settings;
  const patch = (updates: Partial<InquiriesSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Colour"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Colour"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        onChange={(value) => patch({ textContent: value })}
      />
      <SettingsTextInput
        label="EMAIL"
        value={s.email}
        disabled={disabled}
        onChange={(value) => patch({ email: value })}
      />
      <SettingsTextInput
        label="PHONE NUMBER"
        value={s.phoneNumber}
        disabled={disabled}
        onChange={(value) => patch({ phoneNumber: value })}
      />
    </div>
  );
};

const SignaturePanel = ({
  block,
  disabled,
  onUpdate,
}: BlockPanelProps<"signature">) => (
  <div className="space-y-4">
    {/* Design (19) FIELD_DEFS signature: one SIGNATURE field. Saved blocks
        are migrated on load; this also covers a block created in memory. */}
    <BuilderFields
      block={{ ...block, settings: migrateLegacySignatureSettings(block.settings) }}
      disabled={disabled}
      onUpdate={onUpdate}
      fields={[{ key: "text", label: "SIGNATURE", area: true }]}
    />
  </div>
);

const SimpleBlockPanel = ({
  block,
  disabled,
  uploadImage,
  uploadPdf,
  requestDelete,
  onUpdate,
}: BlockPanelDeps & {
  block: EmailBuilderBlock;
  onUpdate: (updates: Record<string, unknown>) => void;
}) => {
  const noun = useCmpOrganizationNoun();
  const s = block.settings as SimpleEmailBlockSettings;
  const type = block.type;
  return (
    <div className="space-y-4">
      {["heading", "text", "button"].includes(type) ||
      // D22 EC-36: the design image block has no side text; saved images
      // with text keep the field.
      (type === "image" && !!s.text.trim()) ? (
        <BuilderFields
          block={block}
          disabled={disabled}
          onUpdate={onUpdate}
          fields={[
            {
              key: "text",
              label:
                type === "button"
                  ? "BUTTON LABEL"
                  : type === "image"
                    ? "TEXT BESIDE IMAGE (OPTIONAL)"
                    : "TEXT",
              area: type !== "button",
              align: s.textAlign,
            },
          ]}
        />
      ) : null}
      {["heading", "text", "button", "divider"].includes(type) ? (
        <ColorSwatchRow
          label={
            type === "divider"
              ? "Line Colour"
              : type === "button"
                ? "Button Colour"
                : "Text Colour"
          }
          color={type === "button" ? s.highlightColor : s.textColor}
          quickPalette={[]}
          swatches={
            type === "divider" ? ["#ECECEC", "#0A0A0A", "#1878F2"] : undefined
          }
          disabled={disabled}
          onChange={(color) =>
            onUpdate(
              type === "button"
                ? { highlightColor: color }
                : { textColor: color },
            )
          }
        />
      ) : null}
      {type === "image" ? (
        <>
          {/* EC-37 / design (16) "IMAGE WIDTH — <n>px": the 600 px email
              width. The saved value stays a percentage (25-100). */}
          <BuilderRange
            label="Image width"
            value={Math.round((s.imageWidth || 100) * 6)}
            min={150}
            max={600}
            step={10}
            unit="px"
            disabled={disabled}
            onChange={(px) => onUpdate({ imageWidth: Math.round((px / 6) * 100) / 100 })}
          />
          <ImageUploadControl
            imageUrl={s.imageUrl}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(imageUrl) => onUpdate({ imageUrl })}
          />
          {s.imageUrl ? (
            <BuilderSegmented
              label="IMAGE DISPLAY"
              value={s.imageFit === "cover" ? "cover" : "contain"}
              options={[
                { value: "cover", label: "Cover Image", title: "Fill the frame; edges may be cropped" },
                { value: "contain", label: "Fit Image", title: "Show the whole image without cropping" },
              ]}
              disabled={disabled}
              onChange={(imageFit) => onUpdate({ imageFit })}
            />
          ) : null}

        </>
      ) : null}
      {type === "button" ? (
        <ButtonTargetControl
          block={block}
          disabled={disabled}
          uploadPdf={uploadPdf}
          requestDelete={requestDelete}
          onUpdate={onUpdate}
        />
      ) : null}
      {type === "divider" ? (
        <BuilderRange
          label="LINE HEIGHT"
          value={s.lineHeight || 1}
          min={1}
          max={12}
          step={1}
          unit="px"
          disabled={disabled}
          onChange={(lineHeight) => onUpdate({ lineHeight })}
        />
      ) : null}
      {type === "spacer" ? (
        <BuilderRange
          label="Height"
          value={s.height}
          min={8}
          max={80}
          step={4}
          unit="px"
          disabled={disabled}
          onChange={(height) => onUpdate({ height })}
        />
      ) : null}
      {type === "socialLinks" ? (
        <p className="text-[13px] text-[#727272]">
          Social links use your current {noun.Singular} Profile. Update them in Manage{" "}
          {noun.Singular} Profile.
        </p>
      ) : null}
    </div>
  );
};

type BuilderFieldSpec = {
  key: string;
  label: string;
  area?: boolean;
  align?: TextAlignOption;
};

// D22 EC-39 / design (16) FIELD_DEFS: only the Urgent Alert Banner offers
// "ADD ADDITIONAL TEXT TO BOTTOM OF BLOCK". A saved block that already has
// bottom text keeps the field, so staff can still edit or clear it.
export const showBuilderField = (block: EmailBuilderBlock, key: string): boolean =>
  key !== "extraText" ||
  block.type === "alertBanner" ||
  !!String((block.settings as { extraText?: unknown }).extraText ?? "").trim();

const BuilderFields = ({
  block,
  fields,
  disabled,
  onUpdate,
}: {
  block: EmailBuilderBlock;
  fields: BuilderFieldSpec[];
  disabled: boolean;
  onUpdate: (updates: Record<string, unknown>) => void;
}) => {
  const settings: Record<string, unknown> = { ...block.settings };
  return (
    <>
      {fields.filter((field) => showBuilderField(block, field.key)).map((field) => {
        const value =
          typeof settings[field.key] === "string"
            ? String(settings[field.key])
            : "";
        const props = {
          label: field.label,
          displayLabel: field.label,
          visibilityKey: field.key,
          value,
          disabled,
          onChange: (text: string) => onUpdate({ [field.key]: text }),
        };
        return field.area ? (
          <RichTextField
            key={field.key}
            {...props}
            defaultTextAlign={field.align || "left"}
          />
        ) : (
          <SettingsTextInput key={field.key} {...props} />
        );
      })}
    </>
  );
};

const PdfAttachmentControl = ({
  pdfUrl,
  pdfFileName,
  disabled,
  uploadPdf,
  onChange,
  requestDelete,
}: {
  pdfUrl: string;
  pdfFileName: string;
  disabled: boolean;
  uploadPdf: (file: File) => Promise<string>;
  onChange: (url: string, name: string) => void;
  requestDelete: BlockPanelDeps["requestDelete"];
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const upload = async (file?: File) => {
    if (!file) return;
    setError("");
    // Same three checks as before; each cause now has its own text.
    if (file.type !== "application/pdf") {
      setError("This file is not a PDF. Choose a PDF file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("This PDF is larger than 10 MB. Choose a smaller PDF.");
      return;
    }
    if (file.size === 0) {
      setError("This PDF is empty. Choose another PDF file.");
      return;
    }
    setLoading(true);
    try {
      onChange(await uploadPdf(file), file.name);
    } catch (error) {
      setError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "upload this PDF",
            "Check your internet connection.",
          ),
        ),
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      {pdfUrl ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-[#ECECEC] px-3 py-[9px]">
          <AttachFileIcon sx={{ fontSize: 15, color: "#F21B1B" }} />
          <span
            className="min-w-0 flex-1 truncate text-[12px] font-semibold"
            title={pdfFileName}
          >
            {pdfFileName || "Attached PDF"}
          </span>
          <button
            type="button"
            aria-label="Remove PDF"
            title="Remove PDF"
            disabled={disabled || loading}
            className="flex h-6 w-6 items-center justify-center rounded-md bg-[#F21B1B] text-white"
            onClick={() =>
              requestDelete(
                () => onChange("", ""),
                "Are you sure you want to remove the attached PDF?",
              )
            }
          >
            <DeleteOutlineIcon sx={{ fontSize: 12 }} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || loading}
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#9E9E9E] bg-white px-[14px] py-2.5 text-[13px] font-semibold disabled:opacity-60"
        >
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l10-10a4 4 0 0 1 5.66 5.66l-10 10a2 2 0 0 1-2.83-2.83l9.19-9.19" />
            </svg>
          )}{" "}
          Attach PDF
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        disabled={disabled || loading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          void upload(file);
        }}
      />
      {error ? (
        <p role="alert" className="mt-1.5 text-[12px] text-[#F21B1B]">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const ButtonTargetControl = ({
  block,
  disabled,
  uploadPdf,
  requestDelete,
  onUpdate,
  linkKey = "addLink",
}: Pick<BlockPanelDeps, "disabled" | "uploadPdf" | "requestDelete"> & {
  block: EmailBuilderBlock;
  onUpdate: (updates: Record<string, unknown>) => void;
  linkKey?: string;
}) => {
  const s: Record<string, unknown> = { ...block.settings };
  return (
    <>
      <SettingsTextInput
        label="BUTTON LINK (URL)"
        value={typeof s[linkKey] === "string" ? String(s[linkKey]) : ""}
        placeholder="https://..."
        disabled={disabled}
        onChange={(url) =>
          onUpdate({ [linkKey]: url, pdfUrl: "", pdfFileName: "" })
        }
      />
      <SettingsField label="OR ATTACH A PDF">
        <PdfAttachmentControl
          pdfUrl={block.settings.pdfUrl || ""}
          pdfFileName={block.settings.pdfFileName || ""}
          disabled={disabled}
          uploadPdf={uploadPdf}
          requestDelete={requestDelete}
          onChange={(pdfUrl, pdfFileName) => onUpdate({ pdfUrl, pdfFileName })}
        />
      </SettingsField>
    </>
  );
};

const BuilderRange = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  disabled: boolean;
  onChange: (value: number) => void;
}) => (
  <SettingsField label={`${label.toUpperCase()} — ${value}${unit}`}>
    <input
      aria-label={label}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      className="w-full accent-[#E1A731]"
      onChange={(event) => onChange(Number(event.target.value))}
    />
  </SettingsField>
);

type NewsroomSource = {
  entries: NewsroomHighlightItem[];
  loading: boolean;
  error: string;
  reload: () => void;
};
const useNewsroomSource = (websiteUrl?: string | null): NewsroomSource => {
  const noun = useCmpOrganizationNoun();
  const [revision, setRevision] = useState(0);
  const [source, setSource] = useState<Omit<NewsroomSource, "reload">>({
    entries: [],
    loading: /^https?:\/\//i.test(normalizeEmailLinkUrl(websiteUrl || "")),
    error: "",
  });
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    const safeUrl = normalizeEmailLinkUrl(websiteUrl || "");
    if (!/^https?:\/\//i.test(safeUrl)) {
      setSource({
        entries: [],
        loading: false,
        // Staff cannot set this address in the dashboard; support sets it.
        error: `Newsroom articles come from your chamber website, and your website address is not set up yet. To add it, email ${STAFF_SUPPORT_EMAIL}.`,
      });
      return;
    }
    const controller = new AbortController();
    setSource({ entries: [], loading: true, error: "" });
    void (async () => {
      try {
        const endpoint =
          process.env.NODE_ENV === "development" &&
          process.env.REACT_APP_CMP_LOCAL_WEBSITE_PORT
            ? `/__cmp_public_newsroom?website=${encodeURIComponent(safeUrl)}`
            : new URL("/api/public/newsroom", safeUrl).toString();
        // A network or CORS failure rejects with the browser's raw text
        // ("Failed to fetch"); show the same message as a failed response.
        const response = await fetch(endpoint, {
          credentials: "omit",
          signal: controller.signal,
        }).catch(() => {
          throw new Error(
            "The public newsroom could not be loaded. Try again.",
          );
        });
        if (!response.ok)
          throw new Error(
            "The public newsroom could not be loaded. Try again.",
          );
        const body: unknown = await response.json();
        if (
          !body ||
          typeof body !== "object" ||
          !("entries" in body) ||
          !Array.isArray(body.entries)
        )
          throw new Error("The public newsroom returned an invalid list.");
        const entries: NewsroomHighlightItem[] = body.entries
          .flatMap((value: unknown) => {
            if (
              !value ||
              typeof value !== "object" ||
              !("id" in value) ||
              typeof value.id !== "string" ||
              !("title" in value) ||
              typeof value.title !== "string" ||
              !("linkUrl" in value) ||
              typeof value.linkUrl !== "string"
            )
              return [];
            const record = value as Record<string, unknown>;
            const text = (key: string) =>
              typeof record[key] === "string" ? (record[key] as string) : "";
            const linkUrl = normalizeEmailLinkUrl(value.linkUrl);
            if (!/^https?:\/\//i.test(linkUrl)) return [];
            return [
              {
                id: value.id,
                title: value.title,
                linkUrl,
                imageUrl: normalizeEmailLinkUrl(text("imageUrl")),
                category: text("category"),
                author: text("author"),
                readTime: text("readTime"),
                sourceAttribution: text("sourceAttribution"),
                publishedAtUtcMilli:
                  typeof record.publishedAtUtcMilli === "number"
                    ? record.publishedAtUtcMilli
                    : null,
              },
            ];
          })
          .sort(
            (a, b) =>
              (b.publishedAtUtcMilli || 0) - (a.publishedAtUtcMilli || 0),
          );
        if (!controller.signal.aborted)
          setSource({ entries, loading: false, error: "" });
      } catch (error) {
        if (!controller.signal.aborted)
          setSource({
            entries: [],
            loading: false,
            // Guard throws above and JSON parse errors show this text.
            error: getUserFacingErrorText(
              error,
              `We could not load the articles from your chamber website newsroom. Check that your website is online, then select Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
            ),
          });
      }
    })();
    return () => controller.abort();
  }, [websiteUrl, revision]);
  // The effect keeps the default wording; the organisation noun is applied here
  // so a noun change does not refetch the newsroom.
  return { ...source, error: applyCmpOrganizationNoun(source.error, noun), reload };
};

const seedBuilderSources = (
  block: EmailBuilderBlock,
  members: EmailAudienceMemberOption[],
  news: NewsroomHighlightItem[],
): EmailBuilderBlock => {
  if (block.type === "newMembers")
    return {
      ...block,
      settings: {
        ...block.settings,
        items: getRecentNewMembers({
          members,
          days: getNewMembersWindowDays(block.settings),
        }),
      },
    };
  if (block.type === "newsroomHighlights")
    return {
      ...block,
      settings: { ...block.settings, items: news.slice(0, 2) },
    };
  return block;
};

const isBuilderSourceLoading = (
  type: EmailBuilderBlockType,
  membersLoading: boolean,
  newsroomLoading: boolean,
): boolean =>
  (type === "newMembers" && membersLoading) ||
  (type === "newsroomHighlights" && newsroomLoading);

type SourcePickerItem = {
  id: string;
  title: string;
  meta: string;
  date: number | null;
  dateChip?: { month: string; day: string };
  logoUrl?: string;
};
const SourcePickerModal = ({
  kind,
  items,
  loading,
  error,
  reload,
  disabled,
  onClose,
  onConfirm,
  single = false,
  initialSelectedId,
  windowDays = NEW_MEMBERS_WINDOW_DAYS,
  maxSelected,
}: {
  kind: "news" | "members" | "events" | "perks" | "perkWeek";
  windowDays?: number;
  /** Most items the user may select (EC-27: a newsroom block holds 6). */
  maxSelected?: number;
  single?: boolean;
  initialSelectedId?: string;
  items: SourcePickerItem[];
  loading: boolean;
  error: string;
  reload?: () => void;
  disabled: boolean;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}) => {
  const noun = useCmpOrganizationNoun();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>(
    initialSelectedId ? [initialSelectedId] : [],
  );
  const withLogo = kind === "members" || kind === "perks" || kind === "perkWeek";
  // Design (16) picker cfg (D@1284593).
  const copy = {
    events: {
      title: single ? "Change Selected Event" : "Add Another Event Card",
      sub: "Select an upcoming event to feature in your newsletter template.",
      search: "Search upcoming events",
      label: "AVAILABLE EVENTS",
      empty: "No events match your search.",
      foot: "Selected events will be automatically styled for email.",
      confirm: single ? "Use Selected Event" : "Add Selected Events",
    },
    news: {
      title: "Add Newsroom Articles",
      sub: `Select news published in the newsroom of the ${noun.singular} website. This block shows a maximum of ${NEWSROOM_BLOCK_MAX_ARTICLES} articles.`,
      search: "Search newsroom articles",
      label: "PUBLISHED ARTICLES",
      empty: "No articles match your search.",
      foot: "Selected articles will be automatically styled for email.",
      confirm: "Add Selected Articles",
    },
    members: {
      title: "Add New Members",
      sub: `Add any additional members that do not appear, from the list below of new members who joined in the last ${windowDays} days.`,
      search: "Search new members",
      label: `NEW MEMBERS (LAST ${windowDays} DAYS)`,
      empty: "No members match your search.",
      foot: "",
      confirm: "Add Selected Members",
    },
    perks: {
      title: "Add Member Perks",
      sub: `The 5 most recent ${noun.Singular} Perks are added automatically. Select any other active perks offered by your members to feature in this email.`,
      search: "Search perks",
      label: "ACTIVE MEMBER PERKS",
      empty: "No perks match your search.",
      foot: "Selected perks will be automatically styled for email.",
      confirm: "Add Selected Perks",
    },
    perkWeek: {
      title: "Select Perk of the Week",
      sub: `Select the ${noun.Singular} Perk to spotlight as this week’s Perk of the Week.`,
      search: "Search perks",
      label: "ACTIVE MEMBER PERKS",
      empty: "No perks match your search.",
      foot: "",
      confirm: "Use Selected Perk",
    },
  }[kind];
  // Design (16) CLAUDE.md: Content Builder pickers filter by starts-with on
  // the name, case-insensitive.
  const visible = items.filter((item) =>
    item.title.trim().toLowerCase().startsWith(search.trim().toLowerCase()),
  );
  const title = copy.title;
  const titleId = useId();
  return (
    <ModalShell
      open
      enableKeyboardFocus
      onClose={onClose}
      showCloseButton={false}
      labelledById={titleId}
      panelClassName="cmp-email-design max-w-[680px] !max-h-[90vh] !overflow-hidden !rounded-[14px] !p-0"
    >
      <div
        style={{ fontFamily: editorFontFamily }}
        className="relative flex max-h-[90vh] flex-col overflow-hidden rounded-[14px] bg-white text-[#0A0A0A]"
      >
        <div className="shrink-0 border-b border-[#ECECEC] px-7 pb-4 pt-[22px]">
          <div className="flex items-center justify-between gap-4">
            <h2 id={titleId} className="pr-5 text-[18px] font-bold">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={`Close ${title}`}
              className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white hover:bg-[#D01414]"
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </button>
          </div>
          <p className="mt-1 text-[14px] leading-[normal] text-[#0A0A0A]">
            {copy.sub}
          </p>
        </div>
        <div className="min-h-0 overflow-y-auto px-7 py-5">
          <div>
            <div className="flex h-[43px] items-center gap-[11px] rounded-lg border border-[#ECECEC] px-[14px]">
              <SearchOutlinedIcon sx={{ fontSize: 15, color: "#9E9E9E" }} />
              <input
                aria-label={copy.search}
                placeholder={copy.search}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="min-w-0 w-full bg-transparent text-[14px] outline-none"
              />
            </div>
          </div>
          <div className="my-4 text-[12px] font-bold tracking-[.05em] text-[#0A0A0A]">
            <span>
              {copy.label}{" "}
              ({items.length})
            </span>
          </div>
          <div className="space-y-4">
            {loading ? (
              <p role="status" className="p-7 text-[13px]">
                Loading...
              </p>
            ) : error ? (
              <div role="alert" className="p-7 text-[13px]">
                <p>{error}</p>
                {reload ? (
                  <button
                    type="button"
                    className="mt-3 font-semibold underline"
                    onClick={reload}
                  >
                    Try again
                  </button>
                ) : null}
              </div>
            ) : visible.length === 0 ? (
              <p className="p-7 text-[13px] text-[#727272]">
                {copy.empty}
              </p>
            ) : (
              visible.map((item) => {
                const chip =
                  item.dateChip ||
                  (item.date ? formatEventChip(item.date) : null);
                const checked = selected.includes(item.id);
                const limitReached =
                  !checked &&
                  !single &&
                  maxSelected !== undefined &&
                  selected.length >= maxSelected;
                return (
                  <div
                    key={item.id}
                    className="flex flex-wrap items-center gap-[18px] rounded-[10px] border border-[#ECECEC] px-[18px] py-3.5"
                  >
                    {withLogo ? (
                      item.logoUrl ? (
                        <img
                          src={item.logoUrl}
                          alt={`${item.title} logo`}
                          className="h-11 w-11 shrink-0 object-contain"
                        />
                      ) : (
                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E1A731] text-[15px] font-bold text-white">
                          {item.title
                            .split(/\s+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((word) => word[0])
                            .join("")
                            .toUpperCase()}
                        </span>
                      )
                    ) : (
                      <div className="flex w-11 shrink-0 flex-col items-center justify-center">
                        <span className="text-[11px] font-bold text-[#727272]">
                          {chip?.month || ""}
                        </span>
                        <span className="text-[18px] font-bold leading-[normal]">
                          {chip?.day || ""}
                        </span>
                      </div>
                    )}
                    <div className="min-w-[min(200px,100%)] flex-1">
                      <p className="text-[15px] font-bold leading-[normal]">
                        {item.title}
                      </p>
                      <p className="mt-1 text-[13px] leading-[normal] text-[#727272]">
                        {item.meta}
                      </p>
                    </div>
                    <span className="flex items-center gap-2.5">
                      {checked ? (
                        <span
                          aria-hidden="true"
                          className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#E1A731] text-white"
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m20 6-11 11-5-5" />
                          </svg>
                        </span>
                      ) : null}
                      <button
                        type="button"
                        aria-label={`${checked ? "Deselect" : "Select"} ${item.title}`}
                        aria-pressed={checked}
                        disabled={disabled || limitReached}
                        title={
                          limitReached
                            ? `You selected the maximum of ${maxSelected} ${
                                kind === "news" ? "articles" : "items"
                              }. Deselect one to choose another.`
                            : undefined
                        }
                        onClick={() =>
                          setSelected((ids) =>
                            checked
                              ? ids.filter((id) => id !== item.id)
                              : single
                                ? [item.id]
                                : [...ids, item.id],
                          )
                        }
                        className={
                          checked
                            ? "rounded-lg bg-[#E1A731] px-5 py-2.5 text-[13px] font-bold text-white"
                            : "rounded-lg bg-[#F0F0F0] px-5 py-2.5 text-[13px] font-bold"
                        }
                      >
                        {checked ? "Selected" : "Select"}
                      </button>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-t border-[#ECECEC] bg-[#EFF5FD] px-7 py-3.5">
          <p className="inline-flex max-w-[240px] items-center gap-2 text-[12px] leading-[1.4]">
            {copy.foot ? (
              <svg
                width="15"
                height="15"
                className="shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            ) : null}
            {copy.foot}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-[#ECECEC] bg-white px-6 py-3 text-[14px] font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={disabled || !selected.length || loading || !!error}
              onClick={() => onConfirm(selected)}
              className="rounded-lg bg-[#E1A731] px-6 py-3 text-[14px] font-bold text-white disabled:opacity-60"
            >
              {copy.confirm}
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

// D22 EC-24: the chamber's active member perks, newest first, as email
// items. Suspended members' perks are left out.
const perkRedeemLabels: Record<WhereToRedeemEnum, string> = {
  [WhereToRedeemEnum.InPerson]: "In Person",
  [WhereToRedeemEnum.Online]: "Online",
  [WhereToRedeemEnum.ContactCompany]: "Contact Company",
};

const usePerkOptions = (chamberId: string) => {
  const timeZone = useChamberTimeZone(chamberId);
  const { data, loading, error, refetch } = useGetCmpEmailBuilderPerksQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const items = useMemo<EmailBuilderPerkItem[]>(
    () =>
      (data?.getAllDiscountsFromChamberId || [])
        .filter(
          (perk) => perk.chamberId === chamberId && !perk.chamberMemberIsSuspended,
        )
        .map((perk) => ({
          id: perk.id,
          title: perk.title,
          company: perk.chamberMemberInfo.name,
          logoUrl: perk.chamberMemberInfo.logo || "",
          description: perk.description || "",
          redeem: perkRedeemLabels[perk.redeemSettings.whereToRedeem] || "",
          validUntil: perk.expiryTimeUtcMilli
            ? `Valid until ${new Date(perk.expiryTimeUtcMilli).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  ...(timeZone ? { timeZone } : {}),
                },
              )}`
            : "",
          createdAtUtcMilli: perk.createdAtUtcMilli ?? null,
          // Perk of the Week phone rows and icons (design (19)).
          ...getPerkWeekDetailFields(perk),
        }))
        .sort(
          (a, b) => (b.createdAtUtcMilli ?? 0) - (a.createdAtUtcMilli ?? 0),
        ),
    [data, chamberId, timeZone],
  );
  return {
    items,
    loading: loading && !data,
    error:
      error && !data
        ? getUserFacingErrorText(
            error,
            buildStaffErrorFallback("load the member perks"),
          )
        : "",
    reload: () => void refetch(),
  };
};

const PerkBlocksPanel = ({
  block,
  disabled,
  chamberId,
  requestDelete,
  onUpdate,
}: BlockPanelDeps & {
  block: EmailBuilderBlockOf<"perks"> | EmailBuilderBlockOf<"perkWeek">;
  onUpdate: (updates: Record<string, unknown>) => void;
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const options = usePerkOptions(chamberId);
  const perksBlock = block.type === "perks" ? block : null;
  // "Auto Add 5 Most Recent Perks": seed once, when the list first loads.
  useEffect(() => {
    if (!perksBlock || perksBlock.settings.seeded || disabled) return;
    if (options.loading || options.error) return;
    onUpdate({ items: options.items.slice(0, 5), seeded: true });
  }, [perksBlock, disabled, options.loading, options.error, options.items, onUpdate]);
  const selectedIds = new Set(
    block.type === "perks"
      ? block.settings.items.map((item) => item.id)
      : block.settings.perk
        ? [block.settings.perk.id]
        : [],
  );
  const available = options.items.filter(
    (item) => block.type === "perkWeek" || !selectedIds.has(item.id),
  );
  // Perk of the Week blocks saved before Oct 2026 move to the design on the
  // first edit (never on read): the fixed gold, and the saved perk gets the
  // phone details (availability, how to redeem, contact, links) when it is
  // still active.
  const onWeekUpdate = (updates: Record<string, unknown>) => {
    if (block.type !== "perkWeek") return onUpdate(updates);
    const saved = block.settings.perk;
    const current =
      saved && saved.socials === undefined && !("perk" in updates)
        ? options.items.find((item) => item.id === saved.id)
        : undefined;
    onUpdate({
      ...updates,
      highlightColor: PERK_WEEK_GOLD,
      ...(current ? { perk: current } : {}),
    });
  };
  return (
    <div className="space-y-4">
      {block.type === "perks" ? (
        <>
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onUpdate}
            fields={[{ key: "title", label: "SECTION TITLE" }]}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => setPickerOpen(true)}
            className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
          >
            + Add Perks
          </button>
          {options.loading && !block.settings.seeded ? (
            <p role="status" className="text-[12px] text-[#727272]">
              Loading member perks...
            </p>
          ) : null}
          {block.settings.seeded && !block.settings.items.length ? (
            <p className="text-[12px] text-[#727272]">
              There are no active member perks yet. When members add perks,
              select + Add Perks to add them here.
            </p>
          ) : null}
          {block.settings.items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-[10px] border border-[#ECECEC] px-3 py-2.5"
            >
              <span className="min-w-0 text-[13px]">
                <span className="block truncate font-semibold">{item.title}</span>
                <span className="block truncate text-[12px] text-[#727272]">
                  {item.company}
                </span>
              </span>
              <button
                type="button"
                aria-label={`Remove ${item.title}`}
                disabled={disabled}
                onClick={() =>
                  requestDelete(() =>
                    onUpdate({
                      items: block.settings.items.filter(
                        (current) => current.id !== item.id,
                      ),
                    }),
                  )
                }
                className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-[#F21B1B] text-white hover:bg-[#D01414]"
              >
                <DeleteOutlineIcon sx={{ fontSize: 13 }} />
              </button>
            </div>
          ))}
        </>
      ) : (
        <>
          {/* Design (19): TITLE, SUBTITLE, HEADLINE, PERK LINK (URL), the
              selected perk and Select Perk; no colour (fixed gold). */}
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onWeekUpdate}
            fields={[
              { key: "label", label: "TITLE" },
              { key: "subtitle", label: "SUBTITLE" },
              { key: "headline", label: "HEADLINE" },
            ]}
          />
          <SettingsTextInput
            label="PERK LINK (URL)"
            visibilityKey="linkUrl"
            value={block.settings.linkUrl}
            placeholder="https://..."
            disabled={disabled}
            onChange={(linkUrl) => onWeekUpdate({ linkUrl })}
          />
          <div className="flex flex-col gap-1 rounded-[10px] border border-[#ECECEC] p-3">
            <span className="text-[11px] font-bold tracking-[.06em] text-[#727272]">
              SELECTED PERK
            </span>
            <span className="text-[13px] font-bold text-[#0A0A0A]">
              {block.settings.perk?.title || "No perk selected"}
            </span>
            {block.settings.perk ? (
              <span className="text-[12px] text-[#727272]">
                {[block.settings.perk.company, block.settings.perk.validUntil]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setPickerOpen(true)}
            className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
          >
            Select Perk
          </button>
        </>
      )}
      {block.type === "perks" ? (
        <ColorSwatchRow
          label="Highlight Colour"
          color={block.settings.highlightColor}
          quickPalette={[]}
          swatches={["#1878F2", "#E1A731", "#10B981", "#0A0A0A"]}
          disabled={disabled}
          onChange={(highlightColor) => onUpdate({ highlightColor })}
        />
      ) : null}
      {pickerOpen ? (
        <SourcePickerModal
          kind={block.type === "perks" ? "perks" : "perkWeek"}
          single={block.type === "perkWeek"}
          initialSelectedId={
            block.type === "perkWeek" ? block.settings.perk?.id : undefined
          }
          items={available.map((item) => ({
            id: item.id,
            title: item.title,
            meta: [item.company, item.validUntil].filter(Boolean).join(" · "),
            date: null,
            logoUrl: item.logoUrl,
          }))}
          loading={options.loading}
          error={options.error}
          reload={options.reload}
          disabled={disabled}
          onClose={() => setPickerOpen(false)}
          onConfirm={(ids) => {
            const picked = options.items.filter((item) => ids.includes(item.id));
            if (block.type === "perks")
              onUpdate({ items: [...block.settings.items, ...picked] });
            else if (picked[0]) onWeekUpdate({ perk: picked[0] });
            setPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

const SourceBlocksPanel = ({
  block,
  disabled,
  memberOptions = [],
  memberOptionsLoading = false,
  newsroomSource,
  requestDelete,
  onUpdate,
}: BlockPanelDeps & {
  block:
    | EmailBuilderBlockOf<"newsroomHighlights">
    | EmailBuilderBlockOf<"newMembers">;
  onUpdate: (updates: Record<string, unknown>) => void;
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const news = block.type === "newsroomHighlights";
  const candidates: Array<NewsroomHighlightItem | NewMemberItem> =
    block.type === "newMembers"
      ? getNewMembersPickerCandidates({
          members: memberOptions,
          days: getNewMembersWindowDays(block.settings),
        })
      : newsroomSource.entries;
  const membersBlock = block.type === "newMembers" ? block : null;
  const excludedMemberIds = membersBlock?.settings.excludedMemberIds || [];
  const includedMemberIds = membersBlock?.settings.includedMemberIds || [];
  // C3: the member list is copied when the block is added. Refresh it when
  // the block is opened in the builder, from the member list already loaded.
  const refreshedBlockRef = useRef("");
  useEffect(() => {
    if (!membersBlock || disabled || memberOptionsLoading || !memberOptions.length) return;
    if (refreshedBlockRef.current === membersBlock.id) return;
    refreshedBlockRef.current = membersBlock.id;
    const items = refreshNewMembersItems({ settings: membersBlock.settings, members: memberOptions });
    if (items) onUpdate({ items });
  }, [membersBlock, disabled, memberOptionsLoading, memberOptions, onUpdate]);
  const currentItems: Array<NewsroomHighlightItem | NewMemberItem> =
    block.settings.items;
  const selectedIds = new Set(currentItems.map((item) => item.id));
  const available = candidates.filter((item) => !selectedIds.has(item.id));
  const newsroomFull =
    news && currentItems.length >= NEWSROOM_BLOCK_MAX_ARTICLES;
  const pickerItems = available.map((item) =>
    "title" in item
      ? {
          id: item.id,
          title: item.title,
          date: item.publishedAtUtcMilli,
          meta: [
            item.category,
            item.publishedAtUtcMilli
              ? new Date(item.publishedAtUtcMilli).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
        }
      : {
          id: item.id,
          title: item.name,
          date: item.joinedAtUtcMilli,
          logoUrl: item.logoUrl,
          meta: [
            item.category,
            item.joinedAtUtcMilli
              ? `Joined ${new Date(item.joinedAtUtcMilli).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
        },
  );
  return (
    <div className="space-y-4">
      <SettingsTextInput
        label="SECTION TITLE"
        value={block.settings.title}
        disabled={disabled}
        onChange={(title) => onUpdate({ title })}
      />
      <button
        type="button"
        disabled={disabled || newsroomFull}
        onClick={() => setPickerOpen(true)}
        className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
      >
        + {news ? "Add News" : "Add Members"}
      </button>
      {newsroomFull ? (
        <p className="text-[12px] text-[#727272]">
          This block shows a maximum of {NEWSROOM_BLOCK_MAX_ARTICLES} articles.
          Remove an article to add another.
        </p>
      ) : null}
      {currentItems.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between gap-2 border-b border-[#ECECEC] pb-[14px]"
        >
          <span className="text-[12px] font-bold uppercase tracking-[.05em] leading-[1.4]">
            {"title" in item ? item.title : item.name}
          </span>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Remove ${"title" in item ? item.title : item.name}`}
            onClick={() =>
              requestDelete(() =>
                onUpdate({
                  items: currentItems.filter(
                    (current) => current.id !== item.id,
                  ),
                  // A later refresh must not re-add a member the user removed.
                  ...(membersBlock
                    ? {
                        excludedMemberIds: Array.from(new Set([...excludedMemberIds, item.id])),
                        includedMemberIds: includedMemberIds.filter((id) => id !== item.id),
                      }
                    : {}),
                }),
              )
            }
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-[#F21B1B] text-white hover:bg-[#D01414]"
          >
            <DeleteOutlineIcon sx={{ fontSize: 13 }} />
          </button>
        </div>
      ))}
      {block.type === "newsroomHighlights" ? (
        <ColorSwatchRow
          label="Button Colour"
          color={block.settings.buttonColor || block.settings.highlightColor}
          quickPalette={[]}
          swatches={["#1878F2", "#E1A731", "#10B981", "#0A0A0A", "#F21B1B"]}
          disabled={disabled}
          onChange={(buttonColor) => onUpdate({ buttonColor })}
        />
      ) : null}
      {news && newsroomSource.error ? (
        <p role="status" className="text-[12px] text-[#727272]">
          We could not load the newsroom articles. Articles already in this
          block stay in this draft. Select Add News to see why and to try
          again.
        </p>
      ) : null}
      {pickerOpen ? (
        <SourcePickerModal
          kind={news ? "news" : "members"}
          maxSelected={
            news
              ? Math.max(0, NEWSROOM_BLOCK_MAX_ARTICLES - currentItems.length)
              : undefined
          }
          windowDays={
            membersBlock
              ? getNewMembersWindowDays(membersBlock.settings)
              : undefined
          }
          items={pickerItems}
          loading={news ? newsroomSource.loading : memberOptionsLoading}
          error={news ? newsroomSource.error : ""}
          reload={news ? newsroomSource.reload : undefined}
          disabled={disabled}
          onClose={() => setPickerOpen(false)}
          onConfirm={(ids) => {
            const items = [
              ...currentItems,
              ...available.filter((item) => ids.includes(item.id)),
            ];
            if (!news)
              items.sort((a, b) =>
                ("name" in a ? a.name : "").localeCompare(
                  "name" in b ? b.name : "",
                ),
              );
            onUpdate({
              items,
              ...(membersBlock
                ? {
                    excludedMemberIds: excludedMemberIds.filter((id) => !ids.includes(id)),
                    // Keep added members through later refreshes, including
                    // members outside the 30-day window.
                    includedMemberIds: Array.from(new Set([...includedMemberIds, ...ids])),
                  }
                : {}),
            });
            setPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

const GalleryPanel = ({
  block,
  disabled,
  uploadImage,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"gallery">) => (
  <div className="space-y-4">
    <SettingsTextInput
      label="SECTION TITLE"
      value={block.settings.title}
      disabled={disabled}
      onChange={(title) => onUpdate({ title })}
    />
    {block.settings.items.map((item, index) => (
      <GridItemCard
        key={item.id}
        label={`IMAGE ${index + 1}`}
        disabled={disabled}
        separated
        onDelete={() =>
          requestDelete(() =>
            onUpdate({
              items: block.settings.items.filter(
                (current) => current.id !== item.id,
              ),
            }),
          )
        }
      >
        <ImageUploadControl
          softRemove
          recommended={[600, 450]}
          imageUrl={item.imageUrl}
          disabled={disabled}
          uploadImage={uploadImage}
          onChange={(imageUrl) =>
            onUpdate({
              items: block.settings.items.map((current) =>
                current.id === item.id ? { ...current, imageUrl } : current,
              ),
            })
          }
        />
        <SettingsTextInput
          label="IMAGE TITLE"
          displayLabel="IMAGE TITLE"
          value={item.title}
          disabled={disabled}
          onChange={(title) =>
            onUpdate({
              items: block.settings.items.map((current) =>
                current.id === item.id ? { ...current, title } : current,
              ),
            })
          }
        />
        <SettingsTextInput
          label="IMAGE LINK (URL)"
          value={item.linkUrl}
          placeholder="https://..."
          disabled={disabled}
          onChange={(linkUrl) =>
            onUpdate({
              items: block.settings.items.map((current) =>
                current.id === item.id ? { ...current, linkUrl } : current,
              ),
            })
          }
        />
        {/* D@441088 (EC-30): optional button per image. */}
        <SettingsTextInput
          label={`IMAGE ${index + 1} BUTTON LABEL (OPTIONAL)`}
          ariaLabel={`IMAGE ${index + 1} BUTTON LABEL (OPTIONAL)`}
          displayLabel="BUTTON LABEL (OPTIONAL)"
          value={item.buttonLabel || ""}
          placeholder="e.g. View Images"
          disabled={disabled}
          onChange={(buttonLabel) =>
            onUpdate({
              items: block.settings.items.map((current) =>
                current.id === item.id ? { ...current, buttonLabel } : current,
              ),
            })
          }
        />
        {(item.buttonLabel || "").trim() ? (
          <SettingsTextInput
            label={`IMAGE ${index + 1} BUTTON LINK (URL)`}
            ariaLabel={`IMAGE ${index + 1} BUTTON LINK (URL)`}
            displayLabel="BUTTON LINK (URL)"
            value={item.buttonUrl || ""}
            placeholder="https://..."
            disabled={disabled}
            onChange={(buttonUrl) =>
              onUpdate({
                items: block.settings.items.map((current) =>
                  current.id === item.id ? { ...current, buttonUrl } : current,
                ),
              })
            }
          />
        ) : null}
      </GridItemCard>
    ))}
    <button
      type="button"
      className="w-full rounded-lg bg-[#E1A731] px-[14px] py-[13px] text-[14px] font-bold text-white hover:bg-[#C6912A] disabled:opacity-60"
      disabled={disabled}
      onClick={() =>
        onUpdate({ items: [...block.settings.items, createGalleryItem()] })
      }
    >
      + Add Image
    </button>
    <ColorSwatchRow
      label="Button Colour"
      color={block.settings.buttonColor || "#1878F2"}
      quickPalette={[]}
      swatches={["#1878F2", "#E1A731", "#10B981", "#0A0A0A", "#F21B1B"]}
      disabled={disabled}
      onChange={(buttonColor) => onUpdate({ buttonColor })}
    />
  </div>
);

const AdditionalBlockPanel = ({
  block,
  disabled,
  uploadImage,
  onUpdate,
  ...deps
}: BlockPanelDeps & {
  block: EmailBuilderBlock;
  onUpdate: (updates: Record<string, unknown>) => void;
}) => {
  switch (block.type) {
    case "advertisementBanner": {
      // What's New / design (16) AD_SIZES: Large (1), Medium (2), Small (3).
      const ad = block.settings;
      const sizeKey = ad.size || "large";
      const size = emailAdBannerSizes[sizeKey];
      const slots = getEmailAdBannerSlots(ad);
      const upload = `${size.width * 2} × ${size.height * 2} px`;
      const setSlot = (index: number, patch: { imageUrl?: string; linkUrl?: string }) => {
        if (index === 0) {
          onUpdate(patch);
          return;
        }
        const extra = [...(ad.extraSlots || [])];
        while (extra.length < index) extra.push({ imageUrl: "", linkUrl: "" });
        extra[index - 1] = { ...extra[index - 1], ...patch };
        onUpdate({ extraSlots: extra });
      };
      return (
        <div className="space-y-4">
          <SettingsField label="SPONSOR SIZE">
            <div role="group" aria-label="SPONSOR SIZE" className="grid grid-cols-3 gap-1 rounded-[8px] border border-[#9E9E9E] p-[3px]">
              {(Object.keys(emailAdBannerSizes) as Array<keyof typeof emailAdBannerSizes>).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={sizeKey === key}
                  disabled={disabled}
                  onClick={() => onUpdate({ size: key })}
                  className={`flex flex-col items-center gap-[1px] rounded-[6px] px-1 py-[7px] ${sizeKey === key ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"}`}
                >
                  <span className="text-[13px] font-semibold">{emailAdBannerSizes[key].label}</span>
                  <span className="text-[10px]">{emailAdBannerSizes[key].tier}</span>
                </button>
              ))}
            </div>
          </SettingsField>
          <p className="-mt-2 text-[12px] leading-[1.5] text-[#0A0A0A]">
            {size.tier}: {size.count === 1 ? "one full-width image" : `${size.count} images side by side`}. Upload {upload} (displays at {size.width} × {size.height} px). JPG or PNG, max 1 MB.
          </p>
          <BuilderSegmented
            label="IMAGE DISPLAY"
            value={ad.imageFit === "cover" || (!ad.imageFit && size.count > 1) ? "cover" : "contain"}
            options={[
              { value: "cover", label: "Cover Image", title: "Fill the frame; edges may be cropped" },
              { value: "contain", label: "Fit Image", title: "Show the whole image without cropping" },
            ]}
            disabled={disabled}
            onChange={(imageFit) => onUpdate({ imageFit })}
          />
          {slots.map((slot, index) => (
            <React.Fragment key={index}>
              <ImageUploadControl
                label={size.count > 1 ? `SPONSOR ${index + 1} IMAGE` : "BANNER IMAGE"}
                softRemove
                recommended={[size.width * 2, size.height * 2]}
                imageUrl={slot.imageUrl}
                disabled={disabled}
                uploadImage={uploadImage}
                onChange={(imageUrl) => setSlot(index, { imageUrl })}
              />
              <SettingsTextInput
                label={size.count > 1 ? `SPONSOR ${index + 1} LINK (URL)` : "BANNER LINK (URL)"}
                value={slot.linkUrl}
                placeholder="https://..."
                disabled={disabled}
                onChange={(linkUrl) => setSlot(index, { linkUrl })}
              />
            </React.Fragment>
          ))}
        </div>
      );
    }
    case "columns":
      return (
        <div className="space-y-4">
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onUpdate}
            fields={[
              { key: "left", label: "LEFT COLUMN", area: true },
              { key: "right", label: "RIGHT COLUMN", area: true },
            ]}
          />
        </div>
      );
    case "footer":
      return (
        <div className="space-y-4">
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onUpdate}
            fields={[
              { key: "organization", label: "ORGANIZATION" },
              { key: "communityName", label: "COMMUNITY NAME" },
            ]}
          />
          {/* FIELD_DEFS footer (EC-33 / D10): FOOTER MESSAGE and Background
              Colour live in the Footer block, not in Step 2. */}
          <SettingsField label="FOOTER MESSAGE">
            <textarea
              aria-label="FOOTER MESSAGE"
              value={deps.footerText ?? ""}
              disabled={disabled || !deps.onFooterChange}
              rows={3}
              onChange={(event) =>
                deps.onFooterChange?.({ footerText: event.target.value })
              }
              className={`${builderInputClassName} min-h-[84px] resize-y`}
            />
          </SettingsField>
          <ColorSwatchRow
            label="Background Colour"
            color={deps.footerBackgroundColor || "#0A0A0A"}
            quickPalette={[]}
            swatches={["#0A0A0A", "#1878F2", "#E1A731", "#10B981", "#F7F7F8"]}
            disabled={disabled || !deps.onFooterChange}
            onChange={(footerBackgroundColor) =>
              deps.onFooterChange?.({ footerBackgroundColor })
            }
          />
        </div>
      );
    case "imageText":
      return (
        <div className="space-y-4">
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onUpdate}
            fields={[
              { key: "text", label: "TEXT", area: true },
              { key: "linkText", label: "LINK TEXT" },
            ]}
          />
          <ColorSwatchRow
            label="Link Colour"
            color={block.settings.highlightColor}
            quickPalette={[]}
            swatches={["#1878F2", "#E1A731", "#10B981"]}
            disabled={disabled}
            onChange={(highlightColor) => onUpdate({ highlightColor })}
          />
          <BuilderRange
            label="Image size"
            value={block.settings.imageSize}
            min={90}
            max={260}
            step={10}
            unit="px"
            disabled={disabled}
            onChange={(imageSize) => onUpdate({ imageSize })}
          />
          <ImageUploadControl
            imageUrl={block.settings.imageUrl}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(imageUrl) => onUpdate({ imageUrl })}
          />
          <SettingsTextInput
            label="LINK URL"
            value={block.settings.linkUrl}
            placeholder="https://..."
            disabled={disabled}
            onChange={(linkUrl) => onUpdate({ linkUrl })}
          />
        </div>
      );
    case "video":
      return (
        <div className="space-y-4">
          {/* FIELD_DEFS video (EC-32): VIDEO URL (required), CAPTION,
              THUMBNAIL IMAGE. */}
          <SettingsTextInput
            label="VIDEO URL"
            value={block.settings.linkUrl}
            placeholder="https://..."
            disabled={disabled}
            onChange={(linkUrl) => onUpdate({ linkUrl })}
          />
          {!block.settings.linkUrl.trim() ? (
            <p role="alert" className="-mt-2 text-[12px] text-[#F21B1B]">
              A video URL is required for the video block.
            </p>
          ) : null}
          <SettingsTextInput
            label="CAPTION"
            value={block.settings.caption}
            disabled={disabled}
            onChange={(caption) => onUpdate({ caption })}
          />
          <ImageUploadControl
            label="THUMBNAIL IMAGE"
            buttonLabel="Replace Thumbnail Image"
            imageUrl={block.settings.imageUrl}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(imageUrl) => onUpdate({ imageUrl })}
          />
          <p className="-mt-2 text-[12px] leading-[1.4] text-[#727272]">
            Shown in the email with a play button. Clicking it opens the video
            URL.
          </p>
        </div>
      );
    case "subscribe":
      // FIELD_DEFS subscribe (EC-24): QUESTION, TEXT, BUTTON LINK (URL),
      // Button Colour.
      return (
        <div className="space-y-4">
          <BuilderFields
            block={block}
            disabled={disabled}
            onUpdate={onUpdate}
            fields={[
              { key: "title", label: "QUESTION", area: true, align: "center" },
              { key: "text", label: "TEXT", area: true, align: "center" },
              { key: "buttonName", label: "BUTTON LABEL" },
            ]}
          />
          <SettingsTextInput
            label="BUTTON LINK (URL)"
            value={block.settings.linkUrl}
            placeholder="https://..."
            disabled={disabled}
            onChange={(linkUrl) => onUpdate({ linkUrl })}
          />
          <ColorSwatchRow
            label="Button Colour"
            color={block.settings.highlightColor}
            quickPalette={[]}
            swatches={["#10B981", "#E1A731", "#1878F2", "#0A0A0A"]}
            disabled={disabled}
            onChange={(highlightColor) => onUpdate({ highlightColor })}
          />
        </div>
      );
    case "logoDate": {
      // Design (19) Logo and Date: DATE, Date Background Colour, LOGO
      // (Replace Logo / Delete Logo), LAYOUT, IMAGE WIDTH. New blocks start
      // with the Step 2 Chamber Logo. A saved block with no logo and no
      // logoRemoved flag shows the Step 2 logo, so the panel shows it too.
      // Moeen (IF8 EC-47): the default width stays 183 px, not the design 160.
      const logoInUse =
        block.settings.logoUrl ||
        (block.settings.logoRemoved ? "" : deps.campaignLogoUrl || "");
      return (
        <div className="space-y-4">
          <SettingsTextInput
            label="DATE"
            visibilityKey="date"
            value={block.settings.date}
            disabled={disabled}
            onChange={(date) => onUpdate({ date })}
          />
          <ColorSwatchRow
            label="Date Background Colour"
            color={block.settings.dateBackgroundColor}
            quickPalette={[]}
            swatches={["#0A0A0A", "#1878F2", "#E1A731", "#10B981", "#F7F7F8"]}
            disabled={disabled}
            onChange={(dateBackgroundColor) => onUpdate({ dateBackgroundColor })}
          />
          <ImageUploadControl
            label="LOGO"
            buttonLabel="Replace Logo"
            uploadButtonLabel="Upload Logo"
            removeButtonLabel="Delete Logo"
            sideBySideActions
            imageUrl={logoInUse}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(logoUrl) => onUpdate({ logoUrl, logoRemoved: false })}
            onRemove={() => onUpdate({ logoUrl: "", logoRemoved: true })}
          />
          <BuilderSegmented
            label="LAYOUT"
            value={block.settings.layout === "centered" ? "centered" : "split"}
            options={[
              { value: "split", label: "Logo Left, Date Right" },
              { value: "centered", label: "Centred" },
            ]}
            disabled={disabled}
            onChange={(layout) => onUpdate({ layout })}
          />
          <BuilderRange
            label="IMAGE WIDTH"
            value={block.settings.logoWidth || 183}
            min={90}
            max={300}
            step={1}
            unit="px"
            disabled={disabled}
            onChange={(logoWidth) => onUpdate({ logoWidth })}
          />
        </div>
      );
    }
    default:
      return null;
  }
};

const BlockSettingsPanel = ({
  block,
  patchBlockSettings,
  ...deps
}: BlockPanelDeps & {
  block: EmailBuilderBlock;
  patchBlockSettings: (
    blockId: string,
    updates: Record<string, unknown>,
  ) => void;
}) => {
  const onUpdateFor = (blockId: string) => (updates: Record<string, unknown>) =>
    patchBlockSettings(blockId, updates);

  switch (block.type) {
    case "perks":
    case "perkWeek":
      return (
        <PerkBlocksPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "newsroomHighlights":
    case "newMembers":
      return (
        <SourceBlocksPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "gallery":
      return (
        <GalleryPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "advertisementBanner":
    case "columns":
    case "footer":
    case "imageText":
    case "video":
    case "subscribe":
    case "logoDate":
      return (
        <AdditionalBlockPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "heading":
    case "text":
    case "image":
    case "button":
    case "divider":
    case "spacer":
    case "socialLinks":
      return (
        <SimpleBlockPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );

    case "textAndButtonHeroBanner":
      return (
        <TextAndButtonHeroBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "textHeroBanner":
      return (
        <TextHeroBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "emailBanner":
      return (
        <EmailBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "updateBanner":
      return (
        <UpdateBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "alertBanner":
      return (
        <AlertBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "advocacyUpdateBanner":
      return (
        <AdvocacyUpdateBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "memberSpotlight":
      return (
        <MemberSpotlightPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "articleList":
      return (
        <ArticleListPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "splitArticleWithBorder":
      return (
        <SplitArticleWithBorderPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "textAndEmailContent":
      return (
        <TextAndEmailContentPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "fourGridIconAndTitle":
      return (
        <FourGridIconAndTitlePanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "sixGridIconAndTitle":
      return (
        <SixGridIconAndTitlePanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "upcomingEventsList":
      return (
        <UpcomingEventsListPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "eventAnnouncement":
      return (
        <EventAnnouncementPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "specialGuestSponsor":
      return (
        <SpecialGuestSponsorPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "sponsorsList":
      return (
        <SponsorsListPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "advertisingPdf":
      return (
        <AdvertisingPdfPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "keyLinks":
      return (
        <KeyLinksPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "inquiries":
      return (
        <InquiriesPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "signature":
      return (
        <SignaturePanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Canvas rendering — visual approximations of each block matching the
// template screenshots (colors bound to the block settings).
// ---------------------------------------------------------------------------

export const getBuilderPanelIntroText = (blockType?: string): string => {
  if (blockType === "newMembers") {
    return "Delete any members you do not want shown.";
  }
  if (blockType === "events" || blockType === "upcomingEventsList") {
    return "Add additional events that you want to appear.";
  }
  if (blockType === "newsroom" || blockType === "newsroomHighlights") {
    return "Delete any articles you do not want shown.";
  }
  return "Use the content builder to add text, images, and updates to each section of your newsletter. Simply click on any block to edit the content, replace images, or customize the layout.";
};

type CanvasImageActions = {
  onImageWidthChange: (width: number) => void;
  onImageResizeStart?: () => void;
  onImageResizeEnd?: (cancel?: boolean) => void;
};
const CanvasBlock = ({
  block,
  footerProfile,
  builderState,
  selected,
  disabled,
  onImageWidthChange,
  onImageResizeStart,
  onImageResizeEnd,
}: CanvasImageActions & {
  block: EmailBuilderBlock;
  footerProfile?: EmailFooterProfile | null;
  builderState: EmailBuilderState;
  selected: boolean;
  disabled: boolean;
}) => {
  const root = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{
    x: number;
    width: number;
    available: number;
    direction: number;
  } | null>(null);
  const [bounds, setBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    if (block.type !== "image" || !selected) {
      setBounds(null);
      return;
    }
    const measure = () => {
      const image = root.current?.querySelector<HTMLElement>(
        "[data-email-builder-image-container]",
      );
      if (!image || !root.current) return;
      const box = image.getBoundingClientRect();
      const outer = root.current.getBoundingClientRect();
      const next = {
        left: box.left - outer.left,
        top: box.top - outer.top,
        width: box.width,
        height: box.height,
      };
      setBounds((current) =>
        current &&
        Object.keys(next).every(
          (key) =>
            next[key as keyof typeof next] ===
            current[key as keyof typeof next],
        )
          ? current
          : next,
      );
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (root.current) observer?.observe(root.current);
    root.current?.addEventListener("load", measure, true);
    window.addEventListener("resize", measure);
    const current = root.current;
    return () => {
      observer?.disconnect();
      current?.removeEventListener("load", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [block, selected]);
  const endResize = (
    event: React.PointerEvent<HTMLButtonElement>,
    cancel = false,
  ) => {
    event.stopPropagation();
    if (!drag.current) return;
    if (cancel) onImageWidthChange(drag.current.width);
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    onImageResizeEnd?.(cancel);
  };
  return (
    <div ref={root} className="relative">
      <div
        className="cmp-email-block-output"
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("a"))
            event.preventDefault();
        }}
        dangerouslySetInnerHTML={{
          __html: renderEmailBuilderBlock(
            block,
            { ...builderState.theme, fontFamily: builderState.fontFamily },
            footerProfile,
            { builderState, editorPreview: true },
          ),
        }}
      />
      {selected && block.type === "spacer" ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] tracking-[.06em] text-[#C9C9C9]">
          SPACER · {block.settings.height}px
        </span>
      ) : null}
      {selected && block.type === "image" && bounds
        ? ([-1, 1] as const).map((direction) => (
            <button
              key={direction}
              type="button"
              aria-label={`Resize image from ${direction === -1 ? "left" : "right"}`}
              disabled={disabled}
              className="absolute z-10 h-6 w-2.5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none rounded-[3px] border border-[#1878F2] bg-white"
              style={{
                left: bounds.left + (direction === 1 ? bounds.width : 0),
                top: bounds.top + bounds.height / 2,
              }}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const available = (root.current?.clientWidth || 640) - 48;
                drag.current = {
                  x: event.clientX,
                  width: block.settings.imageWidth,
                  available,
                  direction,
                };
                event.currentTarget.setPointerCapture?.(event.pointerId);
                onImageResizeStart?.();
              }}
              onPointerMove={(event) => {
                if (!drag.current) return;
                event.stopPropagation();
                const current = drag.current;
                onImageWidthChange(
                  Math.round(
                    clampNumber(
                      current.width +
                        (((event.clientX - current.x) * current.direction) /
                          current.available) *
                          100,
                      25,
                      100,
                    ) / 5,
                  ) * 5,
                );
              }}
              onPointerUp={(event) => endResize(event)}
              onPointerCancel={(event) => endResize(event, true)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
                  return;
                event.preventDefault();
                event.stopPropagation();
                onImageWidthChange(
                  clampNumber(
                    block.settings.imageWidth +
                      (event.key === "ArrowRight" ? 5 : -5),
                    25,
                    100,
                  ),
                );
              }}
            />
          ))
        : null}
    </div>
  );
};

const CanvasBlockShell = ({
  block,
  footerProfile,
  builderState,
  onImageWidthChange,
  onImageResizeStart,
  onImageResizeEnd,
  onPaletteDrop,
  onPaletteDragOver,
  onPaletteDragLeave,
  paletteDropTarget,
  position,
  blockCount,
  movableBlockCount,
  footerCount = 0,
  selected,
  disabled,
  pointerDragging,
  pointerDropTarget,
  keyboardGrabbed,
  onSelect,
  onMove,
  onDuplicate,
  onDelete,
  setMoveHandleRef,
  onPointerReorderStart,
  onPointerReorderMove,
  onPointerReorderEnd,
  onPointerReorderCancel,
  onKeyboardReorder,
  onKeyboardReorderBlur,
}: CanvasImageActions & {
  block: EmailBuilderBlock;
  footerProfile?: EmailFooterProfile | null;
  builderState: EmailBuilderState;
  onPaletteDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onPaletteDragOver?: () => void;
  onPaletteDragLeave?: () => void;
  paletteDropTarget?: boolean;
  position: number;
  blockCount: number;
  movableBlockCount: number;
  /** Footer blocks in the email; the last one cannot be deleted. */
  footerCount?: number;
  selected: boolean;
  disabled: boolean;
  pointerDragging: boolean;
  pointerDropTarget: boolean;
  keyboardGrabbed: boolean;
  onSelect: () => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  setMoveHandleRef: (element: HTMLButtonElement | null) => void;
  onPointerReorderStart: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerReorderMove: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerReorderEnd: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerReorderCancel: (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => void;
  onKeyboardReorder: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onKeyboardReorderBlur: () => void;
}) => {
  const toolbarButtonClassName =
    "flex min-h-11 min-w-11 flex-col items-center justify-center gap-px rounded-[5px] p-[5px] text-[11px] font-bold transition hover:bg-[#33373F] disabled:cursor-not-allowed disabled:opacity-60 xl:[@container(min-width:640px)]:min-h-0 xl:[@container(min-width:640px)]:min-w-0 xl:[@container(min-width:640px)]:w-full";
  const blockLabel = emailBuilderBlockLabels[block.type];
  const isFooter = block.type === "footer";

  return (
    <div
      role="group"
      tabIndex={0}
      data-email-builder-block-id={block.id}
      onDragOver={(event) => {
        event.preventDefault();
        onPaletteDragOver?.();
      }}
      onDragLeave={onPaletteDragLeave}
      onDrop={onPaletteDrop}
      aria-label={`${blockLabel} email block, position ${position + 1} of ${blockCount}`}
      onClick={onSelect}
      onFocus={(event) => {
        if (event.target === event.currentTarget) onSelect();
      }}
      className={`relative my-[3px] rounded-lg cursor-pointer border-[1.5px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold focus-visible:ring-inset ${
        pointerDragging || keyboardGrabbed
          ? "z-20 border-solid border-cpaGold opacity-80 shadow-lg"
          : pointerDropTarget
            ? "border-solid border-cpaGold ring-2 ring-cpaGold ring-inset"
            : selected
              ? "border-solid border-[#1878F2] shadow-[inset_0_0_0_0.5px_#1878F2]"
              : "border-dashed border-transparent hover:border-gray-200"
      }`}
    >
      {pointerDropTarget || paletteDropTarget ? (
        <div
          aria-hidden="true"
          data-palette-drop-indicator
          className="absolute -top-[3px] left-0 right-0 h-[3px] bg-[#E1A731]"
        />
      ) : null}
      {selected ? (
        <>
          <button
            ref={setMoveHandleRef}
            type="button"
            style={{ fontFamily: editorFontFamily }}
            className="absolute -top-[11px] left-[10px] z-20 cursor-grab touch-none rounded bg-[#1878F2] px-2 py-0.5 text-[10px] font-bold tracking-[.04em] text-white active:cursor-grabbing"
            title={isFooter ? "The email footer stays at the end" : "Drag to reorder this block, or use the keyboard and Move Up or Move Down controls"}
            aria-label={`Move ${blockLabel} block, position ${position + 1} of ${blockCount}`}
            aria-describedby={blockReorderInstructionsId}
            aria-pressed={keyboardGrabbed}
            onPointerDown={(event) => {
              event.stopPropagation();
              onPointerReorderStart(event);
            }}
            onPointerMove={onPointerReorderMove}
            onPointerUp={onPointerReorderEnd}
            onPointerCancel={onPointerReorderCancel}
            onKeyDown={onKeyboardReorder}
            onBlur={onKeyboardReorderBlur}
            onClick={(event) => {
              event.stopPropagation();
            }}
            disabled={disabled || isFooter || movableBlockCount < 2}
          >
            Block | {emailBuilderBlockTagLabels[block.type] || blockLabel}
          </button>
          <div
            data-email-builder-block-toolbar
            style={{ fontFamily: editorFontFamily }}
            className="mb-2 ml-auto mr-2 mt-3 flex w-fit flex-row gap-0.5 rounded-[9px] bg-[#26282C] p-1 text-white shadow-[0_2px_8px_rgba(0,0,0,.25)] xl:[@container(min-width:640px)]:absolute xl:[@container(min-width:640px)]:right-2 xl:[@container(min-width:640px)]:top-2 xl:[@container(min-width:640px)]:z-20 xl:[@container(min-width:640px)]:m-0 xl:[@container(min-width:640px)]:flex-col xl:[@container(min-width:640px)]:px-1 xl:[@container(min-width:640px)]:py-[5px]"
          >
            <div className="flex flex-row gap-0.5 xl:[@container(min-width:640px)]:flex-col">
              <button
                type="button"
                className={toolbarButtonClassName}
                title="Move up"
                aria-label={`Move ${blockLabel} block up`}
                onClick={(event) => {
                  event.stopPropagation();
                  onMove(-1);
                }}
                disabled={disabled || isFooter || position === 0}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: 12 }} />
              </button>
              <button
                type="button"
                className={toolbarButtonClassName}
                title="Move down"
                aria-label={`Move ${blockLabel} block down`}
                onClick={(event) => {
                  event.stopPropagation();
                  onMove(1);
                }}
                disabled={disabled || isFooter || position === movableBlockCount - 1}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 12 }} />
              </button>
            </div>
            <button
              type="button"
              title={isFooter ? "The email uses one footer" : "Duplicate"}
              className={toolbarButtonClassName}
              onClick={(event) => {
                event.stopPropagation();
                onDuplicate();
              }}
              disabled={disabled || isFooter}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
              <span className="sr-only">Duplicate</span>
            </button>
            <button
              type="button"
              title={isFooter && footerCount <= 1 ? "The footer is added to every email" : "Delete"}
              className={`${toolbarButtonClassName} !text-[#FF7B7B] hover:!bg-[#5A1E1E]`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              disabled={disabled || (isFooter && footerCount <= 1)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 12 }} />
              <span className="sr-only">Delete</span>
            </button>
          </div>
        </>
      ) : null}
      <CanvasBlock
        selected={selected}
        disabled={disabled}
        onImageWidthChange={onImageWidthChange}
        onImageResizeStart={onImageResizeStart}
        onImageResizeEnd={onImageResizeEnd}
        block={block}
        footerProfile={footerProfile}
        builderState={builderState}
      />
    </div>
  );
};

const CanvasFooter = ({
  builderState,
  footerProfile,
}: {
  builderState: EmailBuilderState;
  footerProfile?: EmailFooterProfile | null;
}) => (
  <div className="overflow-hidden bg-slate-50">
    <EmailFooterPreview
      builderState={builderState}
      footerProfile={footerProfile}
      title="Email builder footer preview"
    />
  </div>
);

// ---------------------------------------------------------------------------
// Builder phase (3-pane) — EMAIL-CREATE-014/015/016
// ---------------------------------------------------------------------------

type EmailBuilderHistoryProps = {
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onHistoryTransactionStart?: () => void;
  onHistoryTransactionEnd?: (cancel?: boolean) => void;
};

// Chamber time zone for builder dates (Logo and Date block).
const useChamberTimeZone = (chamberId: string): string | null => {
  const { data } = useCmpGlobalSettings(chamberId);
  const configured = data?.getChamberMemberProSettings?.setup?.timeZone;
  return configured ? normalizeCmpTimeZone(configured).timeZone : null;
};

// EC-17: the side panels stick below the wizard's sticky header (title and
// stepper), not under it. The header height changes with the error banner
// and the window width, so it is measured.
const useStickyPanelOffset = (): React.CSSProperties => {
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const header = document.querySelector<HTMLElement>(
      '[data-testid="email-wizard-sticky-header"]',
    );
    if (!header) return;
    const measure = () =>
      setHeaderHeight(
        getComputedStyle(header).position === "sticky" ? header.offsetHeight : 0,
      );
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const top = headerHeight + 16;
  return {
    "--cmp-email-panel-top": `${top}px`,
    "--cmp-email-panel-max-height": `calc(100vh - ${top + 24}px)`,
  } as React.CSSProperties;
};

const CustomEmailBuilder = ({
  builderState,
  templateTitle,
  templateBlockTypes,
  disabled,
  chamberId,
  chamberWebsiteUrl,
  footerProfile,
  memberOptions = [],
  memberOptionsLoading = false,
  newsroomSource,
  uploadImage,
  uploadPdf,
  onChange,
  onBackToTemplates,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onHistoryTransactionStart,
  onHistoryTransactionEnd,
}: EmailBuilderHistoryProps & {
  builderState: EmailBuilderState;
  templateTitle: string;
  templateBlockTypes: readonly EmailBuilderBlockType[];
  disabled: boolean;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
  footerProfile?: EmailFooterProfile | null;
  memberOptions?: EmailAudienceMemberOption[];
  memberOptionsLoading?: boolean;
  newsroomSource: NewsroomSource;
  uploadImage: (file: File) => Promise<string>;
  uploadPdf: (file: File) => Promise<string>;
  onChange: UpdateEmailBuilderState;
  onBackToTemplates: () => void;
}) => {
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">(
    "desktop",
  );
  const [settingsTab, setSettingsTab] = useState<"content" | "template">(
    "content",
  );
  const [pendingDelete, setPendingDelete] = useState<{
    onConfirm: () => void;
    message?: string;
  } | null>(null);
  const paletteRef = useRef<HTMLElement | null>(null);
  const stickyPanelStyle = useStickyPanelOffset();
  const chamberTimeZone = useChamberTimeZone(chamberId);
  const [blockSearch, setBlockSearch] = useState("");
  const hasFooterBlock = builderState.blocks.some((block) => block.type === "footer");
  const paletteSections = useMemo(
    () =>
      getEmailBuilderPaletteSections({
        templateBlockTypes,
        search: blockSearch,
        hasFooter: hasFooterBlock,
      }),
    [templateBlockTypes, blockSearch, hasFooterBlock],
  );
  const [paletteDragType, setPaletteDragType] =
    useState<EmailBuilderBlockType | null>(null);
  const [paletteDropTargetId, setPaletteDropTargetId] = useState("");

  const canonicalFooter = getEmailBuilderFooterBlock(builderState.blocks);
  const contentBlocks = useMemo(
    () => builderState.blocks.filter((block) => block.type !== "footer"),
    [builderState.blocks],
  );
  const canvasBlocks = canonicalFooter
    ? [...contentBlocks, canonicalFooter]
    : contentBlocks;
  const storedSelectedBlock =
    builderState.blocks.find(
      (block) => block.id === builderState.selectedBlockId,
    ) || null;
  const selectedBlock =
    storedSelectedBlock?.type === "footer"
      ? canonicalFooter || null
      : storedSelectedBlock;

  const quickPalette = getEmailThemeQuickPalette(builderState.theme);
  const requestDelete = (onConfirm: () => void, message?: string) =>
    setPendingDelete({ onConfirm, message });

  const updateBuilder = (
    updater: (currentState: EmailBuilderState) => EmailBuilderState,
  ) => onChange(updater);

  const sourceIsLoading = (type: EmailBuilderBlockType) =>
    isBuilderSourceLoading(type, memberOptionsLoading, newsroomSource.loading);

  const addBlock = (type: EmailBuilderBlockType, beforeBlockId?: string) => {
    if (disabled || sourceIsLoading(type)) return;
    setSettingsTab("content");
    if (type === "footer" && canonicalFooter) {
      selectBlock({ blockId: canonicalFooter.id });
      return;
    }
    const block = applyNewEmailBuilderBlockDefaults(
      seedBuilderSources(
        createEmailBuilderBlock(type),
        memberOptions,
        newsroomSource.entries,
      ),
      {
        highlightColor: builderState.theme.highlightColor,
        logoUrl: builderState.logoUrl,
        date: formatEmailLogoDate(chamberTimeZone),
      },
    );
    updateBuilder((currentState) => {
      const existingFooter =
        type === "footer"
          ? getEmailBuilderFooterBlock(currentState.blocks)
          : undefined;
      if (existingFooter)
        return { ...currentState, selectedBlockId: existingFooter.id };
      return {
        ...currentState,
        selectedBlockId: block.id,
        blocks:
          beforeBlockId &&
          currentState.blocks.some(
            (current) =>
              current.id === beforeBlockId && current.type !== "footer",
          )
            ? currentState.blocks.flatMap((current) =>
                current.id === beforeBlockId ? [block, current] : [current],
              )
            : [...currentState.blocks, block],
      };
    });
  };

  const selectBlock = ({ blockId }: { blockId: string }) => {
    setSettingsTab("content");
    updateBuilder((currentState) => ({
      ...currentState,
      selectedBlockId:
        currentState.blocks.find((block) => block.id === blockId)?.type ===
        "footer"
          ? getEmailBuilderFooterBlock(currentState.blocks)?.id || ""
          : blockId,
    }));
  };

  const blockReorder = useEmailBuilderBlockReorder({
    blocks: contentBlocks,
    disabled,
    onSelectBlock: selectBlock,
    selectedBlockId: selectedBlock?.id || "",
    updateBuilderState: (update) =>
      onChange((currentState) => {
        const contentState = {
          ...currentState,
          blocks: currentState.blocks.filter((block) => block.type !== "footer"),
        };
        const nextState =
          typeof update === "function" ? update(contentState) : update;
        if (nextState === contentState) return currentState;
        return {
          ...nextState,
          blocks: replaceEmailBuilderContentBlocks(
            currentState.blocks,
            nextState.blocks,
          ),
        };
      }),
    onHistoryTransactionStart,
    onHistoryTransactionEnd,
  });

  const patchBlockSettings = (
    blockId: string,
    updates: Record<string, unknown>,
  ) =>
    updateBuilder((currentState) => {
      const editableId =
        currentState.blocks.find((block) => block.id === blockId)?.type ===
        "footer"
          ? getEmailBuilderFooterBlock(currentState.blocks)?.id || blockId
          : blockId;
      return {
        ...currentState,
        selectedBlockId: editableId,
        blocks: currentState.blocks.map((block) =>
          block.id === editableId
            ? ({
                ...block,
                settings: { ...block.settings, ...updates },
              } as EmailBuilderBlock)
            : block,
        ),
      };
    });

  const duplicateBlock = (blockId: string) => {
    const sourceIndex = builderState.blocks.findIndex(
      (block) => block.id === blockId,
    );
    const source = builderState.blocks[sourceIndex];
    if (!source) return;
    if (source.type === "footer") {
      selectBlock({ blockId: source.id });
      return;
    }
    const duplicated = JSON.parse(JSON.stringify(source)) as EmailBuilderBlock;
    duplicated.id = `${source.type}-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2, 8)}`;
    updateBuilder((currentState) => ({
      ...currentState,
      selectedBlockId: duplicated.id,
      blocks: [
        ...currentState.blocks.slice(0, sourceIndex + 1),
        duplicated,
        ...currentState.blocks.slice(sourceIndex + 1),
      ],
    }));
  };

  const deleteBlock = (blockId: string) =>
    updateBuilder((currentState) => {
      const remaining = currentState.blocks.filter(
        (block) => block.id !== blockId,
      );
      const deletingFooter =
        currentState.blocks.find((block) => block.id === blockId)?.type ===
        "footer";
      return {
        ...currentState,
        selectedBlockId:
          currentState.selectedBlockId === blockId || deletingFooter
            ? (deletingFooter
                ? getEmailBuilderFooterBlock(remaining)?.id
                : undefined) ||
              remaining.find((block) => block.type !== "footer")?.id ||
              getEmailBuilderFooterBlock(remaining)?.id ||
              ""
            : currentState.selectedBlockId,
        blocks: remaining,
      };
    });

  const previewToggleButtonClassName = (active: boolean) =>
    `inline-flex h-8 items-center gap-2 rounded-[6px] px-[14px] text-[13px] font-semibold transition ${
      active ? "bg-[#0A0A0A] text-white" : "bg-transparent text-[#727272] hover:bg-gray-100"
    }`;
  const historyButtonClassName =
    "inline-flex h-9 items-center gap-2 rounded-[8px] border border-[#9E9E9E] bg-white px-[14px] text-[13px] font-semibold text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:opacity-35";

  return (
    <div className="space-y-[14px]">
      <div>
        <button
          type="button"
          onClick={onBackToTemplates}
          disabled={disabled}
          className="inline-flex items-center gap-2 text-[14px] font-semibold text-gray-950 transition hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowBackIcon fontSize="small" />
          Back to Templates
        </button>
        <h2 className="mt-4 text-[16px] font-semibold text-gray-950">
          Email Template: <strong>{templateTitle.toUpperCase()}</strong>
        </h2>
      </div>

      {/* D@255582-258900 (EC-18): "Preview Options" with a labelled
          Desktop / Mobile toggle in the centre and labelled Undo / Redo on
          the right. */}
      <div className="cmp-email-builder-toolbar grid grid-cols-1 items-center gap-4 rounded-xl border border-[#ECECEC] bg-white px-[18px] py-[12px] shadow-sm md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <div className="flex flex-wrap items-center justify-center gap-3 md:col-start-2 md:row-start-1">
          <span className="text-[14px] font-semibold text-[#0A0A0A]">Preview Options</span>
          <div className="inline-flex gap-1 rounded-[9px] border border-[#9E9E9E] bg-white p-[3px]">
            <button
              type="button"
              title="Desktop preview"
              aria-label="Desktop preview"
              aria-pressed={previewMode === "desktop"}
              className={previewToggleButtonClassName(previewMode === "desktop")}
              onClick={() => setPreviewMode("desktop")}
            >
              <DesktopWindowsOutlinedIcon sx={{ fontSize: 16 }} />
              Desktop
            </button>
            <button
              type="button"
              title="Mobile preview"
              aria-label="Mobile preview"
              aria-pressed={previewMode === "mobile"}
              className={previewToggleButtonClassName(previewMode === "mobile")}
              onClick={() => setPreviewMode("mobile")}
            >
              <PhoneIphoneOutlinedIcon sx={{ fontSize: 16 }} />
              Mobile
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-[10px] md:col-start-3 md:row-start-1 md:justify-self-end">
          <button
            type="button"
            aria-label="Undo"
            title="Undo last change"
            className={historyButtonClassName}
            disabled={disabled || !canUndo}
            onClick={onUndo}
          >
            <UndoIcon sx={{ fontSize: 16 }} />
            Undo
          </button>
          <button
            type="button"
            aria-label="Redo"
            title="Redo last change"
            className={historyButtonClassName}
            disabled={disabled || !canRedo}
            onClick={onRedo}
          >
            <RedoIcon sx={{ fontSize: 16 }} />
            Redo
          </button>
        </div>
      </div>

      {/* D@259000 (EC-17): the whole page scrolls; Blocks and Update Block
          Contents stay in view (sticky) next to the email. */}
      <div
        className="cmp-email-builder-workspace grid grid-cols-1 gap-4 xl:grid-cols-[250px_minmax(0,1fr)_320px] xl:items-start"
        style={stickyPanelStyle}
      >
        {/* LEFT — Blocks palette */}
        <aside
          ref={paletteRef}
          className="cmp-email-builder-palette rounded-xl border border-[#ECECEC] bg-white px-[14px] py-[18px] xl:sticky xl:top-[var(--cmp-email-panel-top)] xl:max-h-[var(--cmp-email-panel-max-height)] xl:overflow-y-auto"
        >
          <h3 className="text-[14px] font-bold text-gray-950">Blocks</h3>
          <p className="mt-1 text-[13px] leading-[1.5] text-[#0A0A0A]">
            Select a pre-made block and drag it into your newsletter template.
            Click on the block to easily duplicate it or move it. Then use
            Content builder on the right to add or edit content.
          </p>
          {/* D@260074 (EC-21): Search Blocks filters the palette labels. */}
          <div className="relative mt-4">
            <SearchOutlinedIcon
              aria-hidden="true"
              sx={{ fontSize: 16, color: "#727272" }}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              type="text"
              value={blockSearch}
              onChange={(event) => setBlockSearch(event.target.value)}
              placeholder="Search Blocks"
              aria-label="Search Blocks"
              className="w-full rounded-[8px] border border-[#9E9E9E] bg-white py-[10px] pl-9 pr-3 text-[14px] text-[#0A0A0A] outline-none"
            />
          </div>
          {paletteSections.length === 0 ? (
            <p className="mt-4 text-[13px] text-[#727272]">
              No blocks match “{blockSearch}”.
            </p>
          ) : null}
          <div className="mt-4 space-y-4">
            {paletteSections.map((section, sectionIndex) => (
              <React.Fragment key={section.heading || `rest-${sectionIndex}`}>
                {section.heading ? (
                  <h4 className="!mt-[6px] border-t border-[#ECECEC] pt-[14px] text-[13px] font-bold">
                    {section.heading}
                  </h4>
                ) : sectionIndex > 0 ? (
                  <div aria-hidden="true" className="!mt-[6px] border-t border-[#ECECEC]" />
                ) : null}
                {section.items.map((item) => {
                  const footerAdded = item.key === "footer" && !!canonicalFooter;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      draggable={!disabled && !sourceIsLoading(item.key) && !footerAdded}
                      disabled={disabled || sourceIsLoading(item.key)}
                      onDragStart={(event) => {
                        if (disabled || sourceIsLoading(item.key) || footerAdded) {
                          event.preventDefault();
                          return;
                        }
                        event.dataTransfer.setData("text/plain", item.key);
                        setPaletteDragType(item.key);
                      }}
                      onDragEnd={() => {
                        setPaletteDragType(null);
                        setPaletteDropTargetId("");
                      }}
                      onClick={() => addBlock(item.key)}
                      title={
                        sourceIsLoading(item.key)
                          ? item.key === "newMembers"
                            ? "Loading new members..."
                            : "Loading newsroom articles..."
                          : footerAdded
                            ? "The footer is already in this email"
                            : "Drag into the email or click to add"
                      }
                      aria-label={`Add ${emailBuilderBlockLabels[item.key]}`}
                      className="block w-full text-left disabled:opacity-60"
                    >
                      <p className="mb-1.5 text-[12px] font-semibold leading-4 text-gray-950">
                        {item.label}
                        {sourceIsLoading(item.key) ? " (Loading...)" : ""}
                      </p>
                      {footerAdded ? (
                        <p className="mb-1.5 text-[12px] text-[#727272]">
                          Already added to this email
                        </p>
                      ) : null}
                      <div
                        className={`w-full overflow-hidden rounded-[10px] border border-[#0A0A0A] bg-white hover:border-[#E1A731] ${
                          footerAdded ? "cursor-not-allowed opacity-50" : "cursor-grab"
                        }`}
                      >
                        <EmailBlockThumbnail type={item.key} />
                      </div>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </aside>

        {/* CENTER — email canvas */}
        <main
          onClick={(event) => {
            if (
              !(event.target as HTMLElement).closest(
                "[data-email-builder-block-id]",
              )
            )
              updateBuilder((current) => ({ ...current, selectedBlockId: "" }));
          }}
          className="cmp-email-builder-canvas min-w-0 rounded-xl border border-[#ECECEC] bg-white px-5 pb-[60px] pt-7 [overflow-x:clip]"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (disabled) return;
            const type = event.dataTransfer.getData(
              "text/plain",
            ) as EmailBuilderBlockType;
            if (emailBuilderPaletteItems.some((item) => item.key === type)) {
              addBlock(type);
            }
            setPaletteDragType(null);
            setPaletteDropTargetId("");
          }}
        >
          <p id={blockReorderInstructionsId} className="sr-only">
            Drag this handle with a mouse or touch to reorder the block. With
            the keyboard, press Space or Enter to pick up the block, use Arrow
            Up and Arrow Down to move it, press Space or Enter to drop it, or
            press Escape to cancel. The separate Move Up and Move Down buttons
            are also available.
          </p>
          <div className="sr-only" aria-live="assertive" aria-atomic="true">
            <span key={blockReorder.announcement.sequence}>
              {blockReorder.announcement.message}
            </span>
          </div>
          <div
            className={`mx-auto min-h-[760px] w-full max-w-full [container-type:inline-size] transition-[width] duration-200 ${
              previewMode === "mobile" ? "!max-w-[390px]" : "!max-w-[640px]"
            }`}
            style={{
              backgroundColor: builderState.theme.backgroundColor,
              fontFamily: `'${builderState.fontFamily || "DM Sans"}', Arial, Helvetica, sans-serif`,
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: renderEmailBuilderHeader(builderState) }} />
            {builderState.blocks.length === 0 ? (
              <div className="mx-7 my-8 rounded-md bg-gray-50 px-8 py-14 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-950 text-white">
                  <ForwardToInboxOutlinedIcon sx={{ fontSize: 32 }} />
                </span>
                <p className="mt-5 text-[26px] font-extrabold text-gray-950">
                  Your Message, Your Way.
                </p>
                <p className="mx-auto mt-2 max-w-sm text-[15px] leading-6 text-gray-600">
                  Use our drag-and-drop builder to create a custom email that
                  fits your audience and your goals.
                </p>
                <button
                  type="button"
                  className="mt-6 inline-flex min-h-[44px] items-center rounded-md bg-[#3B82F6] px-7 text-[14px] font-extrabold tracking-wide text-white transition hover:bg-blue-600"
                  onClick={() =>
                    paletteRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                >
                  Start Building
                </button>
              </div>
            ) : (
              <div>
                {canvasBlocks.map((block, index) => {
                  const reorderBindings = blockReorder.getBlockBindings({
                    blockId: block.id,
                  });

                  return (
                    <CanvasBlockShell
                      key={block.id}
                      block={block}
                      position={index}
                      blockCount={canvasBlocks.length}
                      movableBlockCount={contentBlocks.length}
                      footerCount={
                        builderState.blocks.filter((entry) => entry.type === "footer").length
                      }
                      selected={block.id === selectedBlock?.id}
                      disabled={disabled}
                      onSelect={() => selectBlock({ blockId: block.id })}
                      onDuplicate={() => duplicateBlock(block.id)}
                      onDelete={() =>
                        requestDelete(
                          () => deleteBlock(block.id),
                          `Are you sure you want to delete ${emailBuilderBlockLabels[block.type]}?`,
                        )
                      }
                      footerProfile={footerProfile}
                      builderState={builderState}
                      onImageWidthChange={(imageWidth) =>
                        patchBlockSettings(block.id, { imageWidth })
                      }
                      onImageResizeStart={onHistoryTransactionStart}
                      onImageResizeEnd={onHistoryTransactionEnd}
                      paletteDropTarget={paletteDropTargetId === block.id}
                      onPaletteDragOver={() => {
                        if (paletteDragType && !sourceIsLoading(paletteDragType))
                          setPaletteDropTargetId(block.id);
                      }}
                      onPaletteDragLeave={() => setPaletteDropTargetId("")}
                      onPaletteDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const item = emailBuilderPaletteItems.find(
                          (entry) =>
                            entry.key ===
                            event.dataTransfer.getData("text/plain"),
                        );
                        if (!disabled && item) addBlock(item.key, block.id);
                        setPaletteDragType(null);
                        setPaletteDropTargetId("");
                      }}
                      {...reorderBindings}
                    />
                  );
                })}
              </div>
            )}
            {!canonicalFooter ? (
              <CanvasFooter
                builderState={builderState}
                footerProfile={footerProfile}
              />
            ) : null}
          </div>
        </main>

        {/* RIGHT — settings panel */}
        <aside className="cmp-email-builder-settings min-w-0 rounded-xl border border-[#ECECEC] bg-white xl:sticky xl:top-[var(--cmp-email-panel-top)] xl:max-h-[var(--cmp-email-panel-max-height)] xl:overflow-y-auto">
          <div className="px-[18px] pt-[18px]">
            <h3 className="text-[15px] font-bold text-gray-950">
              Update Block Contents
            </h3>
            <p className="mt-1 text-[13px] leading-[1.5] text-[#0A0A0A]">
              {getBuilderPanelIntroText(selectedBlock?.type)}
            </p>
            <div className="mt-[14px] flex gap-1 border-b border-[#ECECEC]">
              <button
                type="button"
                onClick={() => setSettingsTab("content")}
                className={`px-[14px] pb-[11px] pt-[9px] text-[13px] font-semibold transition ${
                  settingsTab === "content"
                    ? "border-b-[3px] border-cpaGold text-cpaGold"
                    : "text-gray-700 hover:text-gray-950"
                }`}
              >
                Content Builder
              </button>
              <button
                type="button"
                onClick={() => setSettingsTab("template")}
                className={`px-[14px] pb-[11px] pt-[9px] text-[13px] font-semibold transition ${
                  settingsTab === "template"
                    ? "border-b-[3px] border-cpaGold text-cpaGold"
                    : "text-gray-700 hover:text-gray-950"
                }`}
              >
                Template Settings
              </button>
            </div>
          </div>
          <div className="space-y-4 bg-white px-[18px] pb-[30px] pt-4">
            {settingsTab === "template" ? (
              <div className="space-y-[14px]">
                <p className="text-[13px] leading-[1.5] text-[#0A0A0A]">
                  Template-wide settings apply to the whole email.
                </p>
                <ColorSwatchRow
                  label="Accent Colour"
                  color={builderState.theme.highlightColor}
                  quickPalette={["#1878F2", "#E1A731", "#10B981", "#0A0A0A"]}
                  disabled={disabled}
                  onChange={(highlightColor) =>
                    updateBuilder((current) => ({
                      ...current,
                      theme: { ...current.theme, highlightColor },
                      blocks: current.blocks.map((block) =>
                        // Perk of the Week stays fixed gold (design (19)).
                        block.type === "perkWeek"
                          ? block
                          : block.type === "alertBanner"
                          ? {
                              ...block,
                              settings: {
                                ...block.settings,
                                backgroundColor: highlightColor,
                              },
                            }
                          : "highlightColor" in block.settings
                            ? ({
                                ...block,
                                settings: { ...block.settings, highlightColor },
                              } as EmailBuilderBlock)
                            : block,
                      ),
                    }))
                  }
                />
                <SettingsTextInput
                  label="NEWSLETTER LABEL"
                  value={builderState.newsletterLabel || ""}
                  disabled={disabled}
                  onChange={(newsletterLabel) =>
                    updateBuilder((current) => ({
                      ...current,
                      newsletterLabel,
                    }))
                  }
                />
              </div>
            ) : selectedBlock ? (
              <BlockFieldContext.Provider
                key={selectedBlock.id}
                value={{
                  block: selectedBlock,
                  disabled,
                  patch: (updates) =>
                    patchBlockSettings(selectedBlock.id, updates),
                }}
              >
                <BlockSettingsPanel
                  block={selectedBlock}
                  patchBlockSettings={patchBlockSettings}
                  disabled={disabled}
                  quickPalette={quickPalette}
                  uploadImage={uploadImage}
                  uploadPdf={uploadPdf}
                  requestDelete={requestDelete}
                  chamberId={chamberId}
                  chamberWebsiteUrl={chamberWebsiteUrl}
                  campaignLogoUrl={builderState.logoUrl}
                  memberOptions={memberOptions}
                  memberOptionsLoading={memberOptionsLoading}
                  newsroomSource={newsroomSource}
                  footerText={builderState.footerText}
                  footerBackgroundColor={builderState.theme.footerBackgroundColor}
                  onFooterChange={({ footerText, footerBackgroundColor }) =>
                    updateBuilder((current) => ({
                      ...current,
                      ...(footerText !== undefined ? { footerText } : {}),
                      ...(footerBackgroundColor !== undefined
                        ? { theme: { ...current.theme, footerBackgroundColor } }
                        : {}),
                    }))
                  }
                />
                {[
                  "advocacyUpdateBanner",
                  "splitArticleWithBorder",
                  "alertBanner",
                  "updateBanner",
                  "keyLinks",
                  "eventAnnouncement",
                  "specialGuestSponsor",
                  "signature",
                  "heading",
                  "text",
                  "button",
                  "columns",
                ].includes(selectedBlock.type) && selectedBlock.settings.topImageUrl ? (
                    <div className="space-y-4">
                      <SettingsField
                        label={`IMAGE SIZE — ${selectedBlock.settings.imageWidth || 100}%`}
                      >
                        <input
                          aria-label="Top image size"
                          type="range"
                          min={25}
                          max={100}
                          step={5}
                          value={selectedBlock.settings.imageWidth || 100}
                          disabled={disabled}
                          className="w-full accent-[#E1A731]"
                          onChange={(event) =>
                            patchBlockSettings(selectedBlock.id, {
                              imageWidth: Number(event.target.value),
                            })
                          }
                        />
                      </SettingsField>
                      <ImageUploadControl
                        imageUrl={selectedBlock.settings.topImageUrl || ""}
                        disabled={disabled}
                        uploadImage={uploadImage}
                        onChange={(topImageUrl) =>
                          patchBlockSettings(selectedBlock.id, { topImageUrl })
                        }
                      />
                    </div>
                ) : null}
              </BlockFieldContext.Provider>
            ) : (
              <p className="text-[13px] leading-[1.55] text-[#727272]">
                Click a block in the newsletter to edit its content here.
              </p>
            )}
          </div>
        </aside>
      </div>

      <DeleteConfirmModal
        open={!!pendingDelete}
        message={pendingDelete?.message}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          pendingDelete?.onConfirm();
          setPendingDelete(null);
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Template gallery (EMAIL-007 — verbatim per the two gallery screenshots)
// ---------------------------------------------------------------------------

const TemplateGallery = ({
  disabled,
  selectedKey,
  onSelect,
}: {
  disabled: boolean;
  selectedKey: EmailTemplateKey | null;
  onSelect: (preset: EmailTemplatePreset) => void;
}) => (
  <section className={sectionClassName}>
    {/* D@250743 (EC-16): 3 columns, 6 cards. */}
    <div className="cmp-email-template-gallery grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
      {emailGalleryTemplatePresets().map((preset, index) => (
        <div
          key={preset.key}
          className="grid min-w-0 grid-rows-[auto_auto_1fr] content-start gap-3"
        >
          <h3 className="text-[16px] font-bold uppercase">
            {index + 1}. {preset.title}
          </h3>
          <p className="text-[14px] leading-[1.45]">{preset.description}</p>
          <div
            className={`flex flex-col overflow-hidden rounded-lg border-[1.5px] ${selectedKey === preset.key ? "border-[#E1A731] bg-[#FDF8EE]" : "border-[#ECECEC] bg-white"}`}
          >
            <p className="border-b border-[#ECECEC] px-3 py-2.5 text-[12px] font-semibold">
              Content Builder - Template Preview
            </p>
            <div className="flex flex-col items-center gap-2.5 p-[14px]">
              <div className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-[#C9C9C9] p-3">
                <div className="flex w-[88%] flex-col items-center gap-1.5 rounded bg-[#1878F2] px-2.5 py-3 text-center text-white">
                  <p className="text-[9px] uppercase tracking-[.06em]">
                    {preset.preview.title}
                  </p>
                  <h4 className="text-[12px] font-bold leading-[1.3]">
                    {preset.preview.heading}
                  </h4>
                  <span className="rounded-[3px] bg-white px-2.5 py-1 text-[9px] font-semibold text-[#1878F2]">
                    {preset.preview.button}
                  </span>
                </div>
                <span className="h-[7px] w-[70%] rounded bg-[#E7E7E7]" />
                <span className="ml-[6%] h-[7px] w-[52%] self-start rounded bg-[#E7E7E7]" />
              </div>
              <button
                type="button"
                className={`${primaryButtonClassName} !min-h-[39px] !border-none !px-7 !py-2.5 !leading-[normal]`}
                disabled={disabled}
                onClick={() => onSelect(preset)}
              >
                {selectedKey === preset.key ? "Selected" : "Select"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Step 3 root — gallery phase, then builder phase
// ---------------------------------------------------------------------------

export type StepThreeContentProps = EmailBuilderHistoryProps & {
  onBuilderBackChange?: (back: (() => void) | null) => void;
  email: EmailFormState;
  isBusy: boolean;
  isSentRecord: boolean;
  updateBuilderState: UpdateEmailBuilderState;
  uploadImage: (file: File) => Promise<string>;
  uploadPdf: (file: File) => Promise<string>;
  footerProfile?: EmailFooterProfile | null;
  memberOptions?: EmailAudienceMemberOption[];
  memberOptionsLoading?: boolean;
  /** Optional: persists the selected template key onto the form state. */
  updateEmailField?: UpdateEmailField;
};

const StepThreeContent = ({
  onBuilderBackChange,
  email,
  isBusy,
  isSentRecord,
  updateBuilderState,
  uploadImage,
  uploadPdf,
  updateEmailField,
  footerProfile,
  memberOptions = [],
  memberOptionsLoading = false,
  ...historyProps
}: StepThreeContentProps) => {
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const chamberWebsiteUrl = chamber?.chamberMemberProWebsiteUrl;
  const disabled = isBusy || isSentRecord;
  const newsroomSource = useNewsroomSource(chamberWebsiteUrl);

  // Drafts/updates that already picked a template (or carry legacy canvas
  // content) skip straight to the builder; fresh campaigns start at the
  // template gallery.
  const [activeTemplateKey, setActiveTemplateKey] =
    useState<EmailTemplateKey | null>(
      () =>
        email.templateId ||
        (email.builderState.blocks.length > 0 ? "customEmail" : null),
    );
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [pendingTemplate, setPendingTemplate] =
    useState<EmailTemplatePreset | null>(null);
  const [lastSelectedTemplateKey, setLastSelectedTemplateKey] =
    useState<EmailTemplateKey | null>(email.templateId || null);
  const observedTemplateKey = useRef(email.templateId);

  // Update routes hydrate saved newsletter detail asynchronously. If Step 3
  // was already mounted while that detail arrived, keep the persisted canvas
  // instead of leaving the user in the template gallery where choosing a
  // template would replace their draft.
  useEffect(() => {
    if (observedTemplateKey.current !== email.templateId) {
      observedTemplateKey.current = email.templateId;
      setActiveTemplateKey(
        email.templateId ||
          (email.builderState.blocks.length ? "customEmail" : null),
      );
      if (email.templateId) setLastSelectedTemplateKey(email.templateId);
      return;
    }
    if (!activeTemplateKey && email.builderState.blocks.length > 0)
      setActiveTemplateKey(email.templateId || "customEmail");
  }, [activeTemplateKey, email.builderState.blocks.length, email.templateId]);

  const activePreset = emailTemplatePresets.find(
    (preset) => preset.key === activeTemplateKey,
  );
  const chamberTimeZone = useChamberTimeZone(chamberId);
  const templateBlockTypes = useMemo(
    () => activePreset?.orderedBlocks.map((block) => block.type) ?? [],
    [activePreset],
  );

  const applyTemplate = (preset: EmailTemplatePreset) => {
    if (disabled) return;
    const blocks = preset.orderedBlocks
      .map((sourceBlock) =>
        seedBuilderSources(sourceBlock, memberOptions, newsroomSource.entries),
      )
      .map((block) =>
        applyNewEmailBuilderBlockDefaults(block, {
          highlightColor: email.builderState.theme.highlightColor,
          logoUrl: email.builderState.logoUrl,
          date: formatEmailLogoDate(chamberTimeZone),
        }),
      );
    historyProps.onHistoryTransactionStart?.();
    updateBuilderState((currentState) => ({
      ...currentState,
      blocks,
      selectedBlockId: "",
    }));
    updateEmailField?.("templateId", preset.key);
    updateEmailField?.("emailType", emailTypeForTemplate(preset.key));
    setActiveTemplateKey(preset.key);
    setLastSelectedTemplateKey(preset.key);
    setShowTemplateGallery(false);
    setPendingTemplate(null);
    historyProps.onHistoryTransactionEnd?.();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectTemplate = (preset: EmailTemplatePreset) => {
    if (
      preset.key === email.templateId &&
      email.builderState.blocks.length > 0
    ) {
      setShowTemplateGallery(false);
      return;
    }
    if (disabled) return;
    if (email.builderState.blocks.length > 0) setPendingTemplate(preset);
    else applyTemplate(preset);
  };
  const backToTemplates = useCallback(() => {
    setShowTemplateGallery(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    onBuilderBackChange?.(activePreset && !showTemplateGallery ? backToTemplates : null);
    return () => onBuilderBackChange?.(null);
  }, [activePreset, showTemplateGallery, backToTemplates, onBuilderBackChange]);

  if (!activePreset || showTemplateGallery) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className={stepTitleClassName}>Content Builder</h2>
          <p className={`${stepDescriptionClassName} !max-w-none`}>
            Select a pre-designed template optimized to ensure highest open and
            click rates for your {noun.singular} communications. All templates are fully
            customization and mobile-responsive.
          </p>
        </div>
        <TemplateGallery
          disabled={disabled}
          selectedKey={lastSelectedTemplateKey}
          onSelect={selectTemplate}
        />
        <ConfirmDialog
          open={!!pendingTemplate}
          constrainContentWidth
          variant="confirm"
          title="Replace Template?"
          message="This will replace the content currently on your email canvas. You can use Undo to restore it."
          confirmColor="gold"
          confirmLabel="Replace"
          cancelLabel="Cancel"
          onConfirm={() => {
            if (pendingTemplate) applyTemplate(pendingTemplate);
          }}
          onClose={() => setPendingTemplate(null)}
        />
      </div>
    );
  }

  return (
    <>
      {activePreset.key === "memberWelcome" && <p className="mb-4 rounded-lg border border-[#ECECEC] p-4 text-sm">
        Send each member's login details from <Link className="font-semibold underline" to={RoutePaths.chamberAdmin_members_manage} target="_blank" rel="noopener noreferrer">Manage Members</Link>. This campaign contains welcome content. Login details use the member account email process.
      </p>}
      <CustomEmailBuilder
        builderState={email.builderState}
        templateTitle={activePreset.builderTitle}
        templateBlockTypes={templateBlockTypes}
        disabled={disabled}
        chamberId={chamberId}
        chamberWebsiteUrl={chamberWebsiteUrl}
        footerProfile={footerProfile ?? chamber}
        memberOptions={memberOptions}
        memberOptionsLoading={memberOptionsLoading}
        newsroomSource={newsroomSource}
        uploadImage={uploadImage}
        uploadPdf={uploadPdf}
        onChange={updateBuilderState}
        onBackToTemplates={backToTemplates}
        {...historyProps}
      />
    </>
  );
};

export default StepThreeContent;
